// PetDetailsModal.tsx - Responsive Version
// @ts-nocheck - Large legacy file being converted to TypeScript
import React, { useState, useEffect, useRef } from 'react';
import { X, Dog, Cat, PawPrint, CheckCircle, XCircle, Clock, AlertCircle, Info, Edit2, Trash2, Save, Upload, FileText, Download, Eye, Menu, Phone, Globe, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { petService } from '../../api/petService';
import ConfirmationModal from '../common/ConfirmationModal';
import LoadingSpinner from '../common/LoadingSpinner';
import { Pet, Document } from '../../types';
import {
  parseRejectionEntry,
  resolveRejectionReasons,
  RejectionReasonInfo,
} from '../../utils/rejectionReasons';

interface PetDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  petData: Pet | null;
  propertyData?: any;
  onPetUpdated: (pet: Pet) => void;
  onPetDeleted: (petId: string) => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  onPhotoUploaded?: (petId: string, photoFile: File) => void;
  propertySettings?: any;
}

// Phone configuration by country
const countryPhoneConfigs = [
  { code: '+1', country: 'USA', flag: '🇺🇸', maxDigits: 10, format: '(XXX) XXX-XXXX' },
  { code: '+44', country: 'UK', flag: '🇬🇧', maxDigits: 10, format: 'XXXX XXXXXX' },
  { code: '+61', country: 'AUS', flag: '🇦🇺', maxDigits: 9, format: 'XXX XXX XXX' },
  { code: '+91', country: 'IND', flag: '🇮🇳', maxDigits: 10, format: 'XXXXX XXXXX' },
  { code: '+86', country: 'CHN', flag: '🇨🇳', maxDigits: 11, format: 'XXX XXXX XXXX' },
  { code: '+81', country: 'JPN', flag: '🇯🇵', maxDigits: 10, format: 'XX XXXX XXXX' },
  { code: '+49', country: 'GER', flag: '🇩🇪', maxDigits: 11, format: 'XXX XXXXXXXX' },
  { code: '+33', country: 'FRA', flag: '🇫🇷', maxDigits: 9, format: 'X XX XX XX XX' },
  { code: '+39', country: 'ITA', flag: '🇮🇹', maxDigits: 10, format: 'XXX XXXXXXX' },
  { code: '+34', country: 'ESP', flag: '🇪🇸', maxDigits: 9, format: 'XXX XXX XXX' },
  { code: '+7', country: 'RUS', flag: '🇷🇺', maxDigits: 10, format: 'XXX XXX-XX-XX' },
  { code: '+55', country: 'BRA', flag: '🇧🇷', maxDigits: 11, format: 'XX XXXXX-XXXX' },
  { code: '+52', country: 'MEX', flag: '🇲🇽', maxDigits: 10, format: 'XX XXXX XXXX' },
  { code: '+82', country: 'KOR', flag: '🇰🇷', maxDigits: 10, format: 'XX XXXX XXXX' },
  { code: '+1-CA', country: 'CAN', flag: '🇨🇦', maxDigits: 10, format: '(XXX) XXX-XXXX' }
];

async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Pet breed options
const dogBreeds = [
  'Akita',
  'Alaskan Malamute',
  'American Staffordshire Terrier',
  'Anatolian Shepherd',
  'Australian Shepherd',
  'Basset Hound',
  'Beagle',
  'Belgian Malinois',
  'Belgian Shepherd',
  'Bernese Mountain Dog',
  'Bichon Frise',
  'Boston Terrier',
  'Boxer',
  'Briard',
  'Brittany Spaniel',
  'Bulldog',
  'Bull Terrier',
  'Cane Corso',
  'Cavalier King Charles Spaniel',
  'Chihuahua',
  'Chow Chow',
  'Collie',
  'Dachshund',
  'Doberman Pinscher',
  'English Cocker Spaniel',
  'English Springer Spaniel',
  'French Bulldog',
  'German Shepherd',
  'German Shorthaired Pointer',
  'Golden Retriever',
  'Great Dane',
  'Havanese',
  'Labrador Retriever',
  'Maltese',
  'Mastiff',
  'Miniature Schnauzer',
  'Newfoundland',
  'Papillon',
  'Pembroke Welsh Corgi',
  'Pit Bull',
  'Pomeranian',
  'Poodle',
  'Presa Canario',
  'Pug',
  'Rhodesian Ridgeback',
  'Rottweiler',
  'Shetland Sheepdog',
  'Shih Tzu',
  'Siberian Husky',
  'Staffordshire Bull Terrier',
  'Weimaraner',
  'West Highland White Terrier',
  'Yorkshire Terrier',
  'Other'
];

const catBreeds = [
  'Abyssinian', 'American Shorthair', 'Bengal', 'Birman', 'British Shorthair', 'Burmese',
  'Devon Rex', 'Domestic Shorthair', 'Exotic Shorthair', 'Himalayan', 'Maine Coon', 'Manx',
  'Munchkin', 'Norwegian Forest Cat', 'Oriental Shorthair', 'Persian', 'Ragdoll', 'Russian Blue',
  'Savannah', 'Scottish Fold', 'Siamese', 'Sphynx', 'Tonkinese', 'Turkish Angora', 'Mixed Breed',
  'Other'
];

const miniatureHorseBreeds = [
  'American Miniature Horse', 'Falabella', 'Miniature Shetland Pony', 'Noma',
  'South African Miniature', 'Yonaguni', 'Other'
];

const otherPetTypes = [
  'Alpaca', 'Axolotl', 'Bearded Dragon', 'Bird', 'Canary', 'Chameleon',
  'Chicken', 'Chinchilla', 'Cockatiel', 'Cockatoo', 'Crab', 'Degu',
  'Dove', 'Duck', 'Ferret', 'Finch', 'Fish', 'Frog', 'Gecko',
  'Gerbil', 'Goat', 'Goldfish', 'Guinea Pig', 'Hamster', 'Hedgehog',
  'Hermit Crab', 'Horse', 'Iguana', 'Lizard', 'Lovebird', 'Macaw',
  'Miniature Horse', 'Miniature Pig', 'Mouse', 'Newt', 'Parakeet',
  'Parrot', 'Pigeon', 'Pot-bellied Pig', 'Rabbit', 'Rat', 'Salamander',
  'Scorpion', 'Sheep', 'Shrimp', 'Snake', 'Spider', 'Sugar Glider',
  'Tarantula', 'Toad', 'Tortoise', 'Turtle'
];

const petColors = [
  'Apricot', 'Beige', 'Black', 'Blue', 'Blue Merle', 'Brindle', 'Brown', 'Chocolate', 'Cream',
  'Fawn', 'Golden', 'Gray', 'Grey', 'Isabella', 'Liver', 'Mahogany', 'Orange', 'Red', 'Red Merle',
  'Rust', 'Sable', 'Silver', 'Tan', 'Tri-color', 'White', 'Yellow', 'Other'
];

const petMarkings = [
  'Blaze (white stripe on face)', 'Boston markings (white chest/face)', 'Collie markings (white collar/chest/legs)',
  'Irish markings (white chest/feet/tip of tail)', 'Mantle (dark with white chest/legs)', 'Mask (dark face)',
  'Merle pattern', 'No markings/Solid color', 'Parti-color (large patches of color)', 'Patches',
  'Piebald (irregular patches)', 'Points (darker ears/muzzle/legs)', 'Roan (mixed colored and white hairs)',
  'Sable pattern', 'Spots', 'Stripes', 'Ticking (small spots throughout coat)', 'Tipped ears',
  'Tuxedo (black with white chest/paws)', 'White chest', 'White feet/socks', 'White markings', 'White patches', 'Other'
];

const PetDetailsModal: React.FC<PetDetailsModalProps> = ({ isOpen, onClose, petData, propertyData, onPetUpdated, onPetDeleted, showToast, onPhotoUploaded, propertySettings }) => {
  // Type-safe pet data access
  const petDataAny = petData as any;
  const [selectedSection, setSelectedSection] = useState<string>('petInfo');
  const [status, setStatus] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedData, setEditedData] = useState<any>({});
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState<boolean>(false);
  const [deactivateReason, setDeactivateReason] = useState<string>('');
  const [deactivateMoveOutDate, setDeactivateMoveOutDate] = useState<string>('');
  const [deactivateNotes, setDeactivateNotes] = useState<string>('');
  const [deactivateAcknowledge, setDeactivateAcknowledge] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [uploadingDocument, setUploadingDocument] = useState<boolean>(false);
  const [documentsModified, setDocumentsModified] = useState<boolean>(false);
  const [modifiedDocTypes, setModifiedDocTypes] = useState<Set<string>>(new Set());
  const [processingDocType, setProcessingDocType] = useState<string | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);
  const [petImageUrl, setPetImageUrl] = useState<string | null>(null);
  const [uploadingPetImage, setUploadingPetImage] = useState<boolean>(false);
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  
  const [customPetTypeMode, setCustomPetTypeMode] = useState(false);
  const [customBreedMode, setCustomBreedMode] = useState(false);
  const [customColorMode, setCustomColorMode] = useState(false);
  const [customMarkingsMode, setCustomMarkingsMode] = useState(false);

  // Pet type autocomplete states
  const [petTypeSuggestions, setPetTypeSuggestions] = useState<string[]>([]);
  const [showPetTypeSuggestions, setShowPetTypeSuggestions] = useState(false);
  const isSelectingPetType = useRef(false);

  // New state for pending photo upload (1 photo)
  const [pendingPhotos, setPendingPhotos] = useState([
    { file: null, preview: null, type: 'front', label: 'Pet Photo' }
  ]);
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  const [previewPhotoIndex, setPreviewPhotoIndex] = useState<number>(0);

  // Carousel states
  const [isCarouselOpen, setIsCarouselOpen] = useState<boolean>(false);
  const [carouselImages, setCarouselImages] = useState<any[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [loadingExistingPhotos, setLoadingExistingPhotos] = useState<boolean>(false);

  // Phone validation states
  const [selectedVetCountryCode, setSelectedVetCountryCode] = useState<string>('+1');
  const [vetPhoneNumber, setVetPhoneNumber] = useState<string>('');
  const [vetPhoneValidationError, setVetPhoneValidationError] = useState<string>('');
  
  // Field validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Address autocomplete states
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const isSelectingAddress = useRef(false);
  const hasUserTypedAddress = useRef(false);

  // Cache selected files locally until Save is clicked
  const [pendingDocFiles, setPendingDocFiles] = useState<Record<string, { file: File; docName: string; hash?: string }>>({});

  const isEsaSa = petData?.pet_role === 'esa' || petData?.pet_role === 'sa';
  const isEsaSaPending = isEsaSa && (petData?.status === 'Pending' || petData?.status === 'PendingPayment');
  const isRejected = petData?.status === 'Rejected';
  const isPetInfoLocked = !isRejected && (petData?.status === 'Active' || (isEsaSa && petData?.status !== 'inactive' && petData?.status !== 'Drafted' && !isEsaSaPending));

  useEffect(() => {
    if (petData) {
      // Parse phone number
      let phoneNumber = (petData as any).vet_phone || petData.vetPhone || '';
      let countryCode = '+1';
      let numberOnly = phoneNumber;
      
      if (phoneNumber.startsWith('+')) {
        const matchingConfig = countryPhoneConfigs.find(config =>
          phoneNumber.startsWith(config.code)
        );
        if (matchingConfig) {
          countryCode = matchingConfig.code;
          numberOnly = phoneNumber.substring(matchingConfig.code.length);
          numberOnly = numberOnly.replace(/\D/g, '');
        }
      } else {
        numberOnly = phoneNumber.replace(/\D/g, '');
      }
      
      setSelectedVetCountryCode(countryCode);
      setVetPhoneNumber(numberOnly);
      setStatus(petData.status);
      
      const petType = petData.pet_type || 'dog';
      const isCustomPetType = !['dog', 'cat', 'miniature_horse'].includes(petType);
      setCustomPetTypeMode(isCustomPetType);

      const savedBreed = petData.breed || '';
      const knownBreeds = petType === 'dog' ? dogBreeds : petType === 'cat' ? catBreeds : petType === 'miniature_horse' ? miniatureHorseBreeds : [];
      setCustomBreedMode(isCustomPetType || (savedBreed && !knownBreeds.includes(savedBreed)));

      const savedColor = petData.color || '';
      setCustomColorMode(savedColor && !petColors.includes(savedColor));

      const savedMarkings = petData.markings || '';
      setCustomMarkingsMode(savedMarkings && !petMarkings.includes(savedMarkings));

      const customFieldData: Record<string, string> = {};
      const petCustomFields = (petData as any).custom_fields || {};
      Object.keys(petCustomFields).forEach(key => {
        customFieldData[`custom_${key}`] = petCustomFields[key] || '';
      });

      setEditedData({
        pet_name: petData.pet_name || '',
        pet_type: petData.pet_type || 'dog',
        breed: savedBreed,
        age: petData.age || 0,
        weight: petData.weight || 0,
        gender: petData.gender || '',
        move_in_date: petData.move_in_date,
        move_out_date: petData.move_out_date,
        spayed_neutered: petData.spayed_neutered,
        color: savedColor,
        markings: savedMarkings,
        vet_clinic: (petData as any).vet_clinic || petData.vetClinic || '',
        vet_phone: phoneNumber,
        vet_address: (petData as any).vet_address || petData.vetAddress || '',
        last_visit: (petData as any).last_visit || petData.lastVisit || '',
        pet_role: (petData as any).pet_role || petData.animalType || 'pet',
        esa_professional: (petData as any).esa_professional || petData.esaProfessional || '',
        esa_letter_date: (petData as any).esa_letter_date || petData.esaLetterDate || '',
        esa_registration: (petData as any).esa_registration || petData.esaRegistration || '',
        previous_pet_ownership:
          (petData as any).previous_pet_ownership === true
            ? 'Yes'
            : (petData as any).previous_pet_ownership === false
              ? 'No'
              : '',
        ...customFieldData,
      });
      setPetImageUrl(petData.image_url || null);
    }
  }, [petData]);

  useEffect(() => {
    if (selectedSection === 'documents' && petData?.id) {
      fetchDocuments();
    }
  }, [selectedSection, petData?.id]);

  // Fetch existing images when entering edit mode
  useEffect(() => {
    if (isEditMode && selectedSection === 'petInfo' && petData?.id) {
      fetchExistingImages();
    }
  }, [isEditMode, selectedSection, petData?.id]);

  // Reset user-typed flag when leaving edit mode or switching sections
  useEffect(() => {
    hasUserTypedAddress.current = false;
  }, [isEditMode, selectedSection]);

  // Debounce vet address autocomplete search
  useEffect(() => {
    if (isSelectingAddress.current) return;
    if (!isEditMode || selectedSection !== 'vetInfo') return;
    if (!hasUserTypedAddress.current) return;
    const timer = setTimeout(() => {
      if (editedData.vet_address) {
        searchAddress(editedData.vet_address);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [editedData.vet_address, isEditMode, selectedSection]);

  // Address autocomplete functionality
  const searchAddress = async (query: string) => {
    if (!query || query.trim().length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    setAddressSearchLoading(true);
    try {
      const url = `https://addresses-dev.getcoveredinsurance.com/lookup?q=${encodeURIComponent(query)}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const suggestions = (data.data || []).map((addr: any) => ({
        source: addr.source,
        display: addr.full_str,
        full: addr.full_str,
        data: addr
      }));
      setAddressSuggestions(suggestions);
      setShowAddressSuggestions(suggestions.length > 0);
    } catch (error) {
      console.error('Address API error:', error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      setAddressSearchLoading(false);
    }
  };

  const handleAddressSelection = (suggestion: any) => {
    isSelectingAddress.current = true;
    handleInputChange('vet_address', suggestion.full);
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    setTimeout(() => { isSelectingAddress.current = false; }, 300);
  };

  // Close address suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAddressSuggestions && !(event.target as HTMLElement).closest('.address-autocomplete-container')) {
        setShowAddressSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAddressSuggestions]);

  const handlePetTypeSearch = (query: string) => {
    handleInputChange('pet_type', query);
    if (!query.trim()) {
      setPetTypeSuggestions([]);
      setShowPetTypeSuggestions(false);
      return;
    }
    const lower = query.toLowerCase();
    const filtered = otherPetTypes.filter(t => t.toLowerCase().includes(lower));
    setPetTypeSuggestions(filtered);
    setShowPetTypeSuggestions(filtered.length > 0);
  };

  const handlePetTypeSelection = (type: string) => {
    isSelectingPetType.current = true;
    handleInputChange('pet_type', type);
    setPetTypeSuggestions([]);
    setShowPetTypeSuggestions(false);
    setTimeout(() => { isSelectingPetType.current = false; }, 300);
  };

  // Close pet type suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showPetTypeSuggestions && !(event.target as HTMLElement).closest('.pet-type-autocomplete-container')) {
        setShowPetTypeSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPetTypeSuggestions]);

  const fetchExistingImages = async () => {
    setLoadingExistingPhotos(true);
    try {
      const response = await petService.getPetImages(petData.id);
      const images = response.images || [];

      if (images.length > 0) {
        // Map images to the three slots (front, side, additional)
        const updatedPhotos = [...pendingPhotos];
        images.forEach((img, index) => {
          if (index < 3) {
            updatedPhotos[index] = {
              ...updatedPhotos[index],
              preview: img.url, // Use the presigned URL from S3
              file: null // No file object since it's already uploaded
            };
          }
        });
        setPendingPhotos(updatedPhotos);
      }
    } catch (error) {
      console.error('Error fetching existing images:', error);
    } finally {
      setLoadingExistingPhotos(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const docs = await petService.getPetDocuments(petData.id);
      setDocuments(docs);
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  if (!isOpen || !petData) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString('en-US', { timeZone: 'UTC' });
    } catch {
      return 'Invalid date';
    }
  };

  // Date window helpers for the q16 "Date of Last Visit" input. Mirrors the
  // wizard's pair of helpers in QuestionnaireModel.tsx (lines 1032 / 1040)
  // so edit-mode enforces the same "within the past year, not in the
  // future" window as the new-pet flow.
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getOneYearAgoDate = () => {
    const today = new Date();
    const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    const year = oneYearAgo.getFullYear();
    const month = String(oneYearAgo.getMonth() + 1).padStart(2, '0');
    const day = String(oneYearAgo.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Not provided';
    return phone;
  };

  const KNOWN_QUESTION_IDS = new Set([
    'q1','q2','q3','q4','q5','q6','q7','q8','q11','q12','q13','q14','q15','q16','q17','q19'
  ]);

  // Single visibility helper — admin's `show: false` wins outright. Every render
  // and validation site routes through here (or through isFieldVisible, which
  // delegates to it).
  const shouldShowQuestion = (q: any) => {
    if (!q) return false;
    if (q.show === false) return false;
    return true;
  };

  // q2 (Animal Type) and q3 (Animal Name) are always required and visible
  // regardless of property settings — they identify the animal.
  const HARDCODED_REQUIRED_VISIBLE = new Set(['q2', 'q3']);

  // Questions hidden for Service Animals regardless of admin settings.
  const HIDE_FOR_SA = new Set(['q4']);

  const isFieldVisible = (questionId: string): boolean => {
    if (HARDCODED_REQUIRED_VISIBLE.has(questionId)) return true;
    if (HIDE_FOR_SA.has(questionId) && petData?.pet_role === 'sa') return false;
    if (!propertySettings?.questionnaire) return true;
    const question = propertySettings.questionnaire.find((q: any) => q.id === questionId);
    if (!question) return false;
    return shouldShowQuestion(question);
  };

  const isFieldRequired = (questionId: string): boolean => {
    if (HARDCODED_REQUIRED_VISIBLE.has(questionId)) return true;
    if (!propertySettings?.questionnaire) return true;
    const question = propertySettings.questionnaire.find((q: any) => q.id === questionId);
    return question ? question.required : true;
  };

  const getFieldLabel = (questionId: string, defaultLabel: string): string => {
    if (!propertySettings?.questionnaire) return defaultLabel;
    const question = propertySettings.questionnaire.find((q: any) => q.id === questionId);
    return question?.question || defaultLabel;
  };

  const getCustomQuestions = (group: string) => {
    if (!propertySettings?.questionnaire) return [];
    return propertySettings.questionnaire
      .filter((q: any) => !KNOWN_QUESTION_IDS.has(q.id) && q.group === group && shouldShowQuestion(q))
      .sort((a: any, b: any) => a.order - b.order);
  };

  const getPetIcon = (petType) => {
    switch (petType?.toLowerCase()) {
      case 'dog':
        return <Dog className="w-4 h-4 text-gray-600" />;
      case 'cat':
        return <Cat className="w-4 h-4 text-gray-600" />;
      case 'miniature_horse':
        return <PawPrint className="w-4 h-4 text-gray-600" />;
      default:
        return <Dog className="w-4 h-4 text-gray-600" />;
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => {
      const updates = {
        ...prev,
        [field]: value
      };
      
      if (field === 'pet_type' && value !== prev.pet_type) {
        updates.breed = '';
        const isStandardType = ['dog', 'cat'].includes(value);
        setCustomBreedMode(!isStandardType);
      }
      
      return updates;
    });
    if (fieldErrors[field]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    }
  };

  const handleVetCountryCodeChange = (code: string) => {
    setSelectedVetCountryCode(code);
    setVetPhoneValidationError('');
    handleInputChange('vet_phone', code + vetPhoneNumber);
  };

  const handleVetPhoneNumberChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    const currentConfig = countryPhoneConfigs.find(c => c.code === selectedVetCountryCode) || countryPhoneConfigs[0];
    const truncatedNumber = digitsOnly.substring(0, currentConfig.maxDigits);
    
    setVetPhoneNumber(truncatedNumber);
    handleInputChange('vet_phone', selectedVetCountryCode + truncatedNumber);
    
    if (vetPhoneValidationError) {
      setVetPhoneValidationError('');
    }
  };

  const validateVetPhoneNumber = (): boolean => {
    const currentConfig = countryPhoneConfigs.find(c => c.code === selectedVetCountryCode) || countryPhoneConfigs[0];
    const isSA = petData?.pet_role === 'sa';

    if (!vetPhoneNumber || vetPhoneNumber.trim() === '') {
      // Vet info is always optional for Service Animals — admin's required flag does not apply.
      if (!isSA && isFieldVisible('q15') && isFieldRequired('q15')) {
        setVetPhoneValidationError('Vet phone number is required');
        return false;
      }
      setVetPhoneValidationError('');
      return true;
    }
    
    if (vetPhoneNumber.length < 7) {
      setVetPhoneValidationError('Phone number is too short');
      return false;
    }
    
    if (vetPhoneNumber.length < currentConfig.maxDigits) {
      setVetPhoneValidationError(`Phone number should be ${currentConfig.maxDigits} digits for ${currentConfig.country}`);
      return false;
    }
    
    setVetPhoneValidationError('');
    return true;
  };

  const hasValidationErrors = () => {
    const checkField = (qId: string, field: string): boolean => {
      if (!isFieldVisible(qId) || !isFieldRequired(qId)) return false;
      const val = editedData[field];
      if (field === 'age') return val === null || val === undefined || val === '';
      if (field === 'spayed_neutered') return val === null || val === undefined;
      return !val && val !== 0;
    };

    if (selectedSection === 'petInfo') {
      if (checkField('q3', 'pet_name')) return true;
      if (checkField('q2', 'pet_type')) return true;
      if (checkField('q4', 'breed')) return true;
      if (checkField('q5', 'weight')) return true;
      if (checkField('q6', 'age')) return true;
      if (checkField('q7', 'gender')) return true;
      if (checkField('q8', 'spayed_neutered')) return true;
      if (checkField('q11', 'color')) return true;
      if (checkField('q12', 'markings')) return true;
      if (isFieldVisible('q19') && isFieldRequired('q19') && !editedData.previous_pet_ownership) return true;
      for (const q of getCustomQuestions('animal_info')) {
        if (q.required && !editedData[`custom_${q.id}`]) return true;
      }
    }

    if (selectedSection === 'vetInfo') {
      // Vet info is always optional for Service Animals — admin's required flag does not apply.
      if (petData?.pet_role === 'sa') return false;
      if (checkField('q13', 'vet_clinic')) return true;
      if (checkField('q14', 'vet_address')) return true;
      if (checkField('q16', 'last_visit')) return true;
      // last_visit must additionally fall within the past-year window
      // when present. Mirrors the wizard's submit-time guard.
      const lv = editedData.last_visit;
      if (lv) {
        const lvStr = typeof lv === 'string' ? lv : new Date(lv).toISOString().split('T')[0];
        if (lvStr > getTodayDate() || lvStr < getOneYearAgoDate()) return true;
      }
      if (isFieldVisible('q15') && isFieldRequired('q15') && !vetPhoneNumber) return true;
      for (const q of getCustomQuestions('vet_info')) {
        if (q.required && !editedData[`custom_${q.id}`]) return true;
      }
    }

    return false;
  };

  // Open carousel and fetch all images
  const openCarousel = async () => {
    setLoadingImages(true);
    try {
      const response = await petService.getPetImages(petData.id);
      const images = response.images || [];

      // If there are images, open the carousel
      if (images.length > 0) {
        setCarouselImages(images);
        setCurrentImageIndex(0);
        setIsCarouselOpen(true);
      }
    } catch (error) {
      console.error('Error fetching pet images:', error);
    } finally {
      setLoadingImages(false);
    }
  };

  // Navigate to next image in carousel
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
  };

  // Navigate to previous image in carousel
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const validateRequiredFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (selectedSection === 'petInfo') {
      const fieldMap: { qId: string; field: string; label: string }[] = [
        { qId: 'q3', field: 'pet_name', label: 'Name' },
        { qId: 'q2', field: 'pet_type', label: 'Type' },
        { qId: 'q4', field: 'breed', label: 'Breed' },
        { qId: 'q5', field: 'weight', label: 'Weight' },
        { qId: 'q6', field: 'age', label: 'Age' },
        { qId: 'q7', field: 'gender', label: 'Gender' },
        { qId: 'q8', field: 'spayed_neutered', label: 'Spayed/Neutered' },
        { qId: 'q11', field: 'color', label: 'Color' },
        { qId: 'q12', field: 'markings', label: 'Markings' },
      ];

      for (const { qId, field, label } of fieldMap) {
        if (!isFieldVisible(qId) || !isFieldRequired(qId)) continue;
        const val = editedData[field];
        if (field === 'age') {
          if (val === null || val === undefined || val === '') {
            errors[field] = `${label} is required`;
          }
        } else if (field === 'spayed_neutered') {
          if (val === null || val === undefined) {
            errors[field] = `${label} is required`;
          }
        } else if (!val && val !== 0) {
          errors[field] = `${label} is required`;
        }

        if (field === 'pet_name' && val) {
          const validPattern = /^[a-zA-Z\s\-']+$/;
          if (!validPattern.test(val)) {
            errors[field] = 'Name can only contain letters, spaces, hyphens, and apostrophes';
          } else if (val.length < 2) {
            errors[field] = 'Name must be at least 2 characters';
          } else if (!/[a-zA-Z]/.test(val)) {
            errors[field] = 'Name must contain at least one letter';
          }
        }
      }

      if (isFieldVisible('q19') && isFieldRequired('q19')) {
        if (!editedData.previous_pet_ownership) {
          errors['previous_pet_ownership'] = 'Previous Pet Ownership is required';
        }
      }

      for (const q of getCustomQuestions('animal_info')) {
        if (q.required && !editedData[`custom_${q.id}`]) {
          errors[`custom_${q.id}`] = `${q.question} is required`;
        }
      }
    }

    if (selectedSection === 'vetInfo' && petData?.pet_role !== 'sa') {
      // Vet info is always optional for Service Animals — skip required-field validation.
      const vetFieldMap: { qId: string; field: string; label: string }[] = [
        { qId: 'q13', field: 'vet_clinic', label: 'Clinic Name' },
        { qId: 'q14', field: 'vet_address', label: 'Address' },
        { qId: 'q16', field: 'last_visit', label: 'Last Visit' },
      ];

      for (const { qId, field, label } of vetFieldMap) {
        if (!isFieldVisible(qId) || !isFieldRequired(qId)) continue;
        if (!editedData[field]) {
          errors[field] = `${label} is required`;
        }
      }

      if (isFieldVisible('q15') && isFieldRequired('q15') && !vetPhoneNumber) {
        errors['vet_phone'] = 'Phone is required';
      }

      for (const q of getCustomQuestions('vet_info')) {
        if (q.required && !editedData[`custom_${q.id}`]) {
          errors[`custom_${q.id}`] = `${q.question} is required`;
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Validate phone format if in vet info section
    if (selectedSection === 'vetInfo' && !validateVetPhoneNumber()) {
      if (showToast) {
        showToast('Please fix phone number validation errors', 'error');
      }
      return;
    }

    // Validate required fields based on questionnaire settings
    if (!validateRequiredFields()) {
      if (showToast) {
        showToast('Please fill in all required fields', 'error');
      }
      return;
    }

    try {
      setIsSaving(true);
      
      // Upload pending photos if any exist
      const photoFiles = pendingPhotos.filter(p => p.file).map(p => p.file);
      if (photoFiles.length > 0) {
        setUploadingPetImage(true);

        // Notify parent to create local blob URL for dashboard (use first photo)
        if (onPhotoUploaded && photoFiles[0]) {
          onPhotoUploaded(petData.id, photoFiles[0]);
        }

        await petService.uploadAnimalImages(petData.id, photoFiles);
        const updatedPetWithImage = await petService.getPetDetails(petData.id);
        if (updatedPetWithImage?.image_url) {
          setPetImageUrl(updatedPetWithImage.image_url);
          // Clear pending photos
          pendingPhotos.forEach(photo => {
            if (photo.preview) {
              URL.revokeObjectURL(photo.preview);
            }
          });
          setPendingPhotos([
            { file: null, preview: null, type: 'front', label: 'Pet Photo' }
          ]);
        } else {
          // Keep showing pending preview if backend image not available yet
          console.warn('Backend image URL not available yet, keeping local preview');
        }
        setUploadingPetImage(false);
      }
      
      // Extract custom_fields from editedData (keys starting with "custom_")
      const customFields: Record<string, string> = {};
      const standardData: Record<string, any> = {};
      Object.entries(editedData).forEach(([key, value]) => {
        if (key.startsWith('custom_')) {
          customFields[key.replace('custom_', '')] = value as string;
        } else {
          standardData[key] = value;
        }
      });
      // q19 (previous_pet_ownership) is a boolean column in the DB but the UI
      // uses a "Yes"/"No" dropdown. Convert back before sending.
      if ('previous_pet_ownership' in standardData) {
        const ppo = standardData.previous_pet_ownership;
        standardData.previous_pet_ownership =
          ppo === 'Yes' ? true : ppo === 'No' ? false : null;
      }
      const dataWithCustomFields = {
        ...standardData,
        ...(Object.keys(customFields).length > 0 ? { custom_fields: customFields } : {}),
      };

      // Update pet data - for Active/Approved pets or ESA/SA submitted pets, only send vet info fields (documents handled separately)
      // Rejected pets should NOT be locked — allow full edits for resubmission
      const currentlyRejected = petData.status === 'Rejected' || petData.status === 'rejected';
      const isLocked = !currentlyRejected && (petData.status === 'Active' || petData.status === 'active' || (isEsaSa && !isEsaSaPending));
      const dataToSend = isLocked
        ? {
            vet_clinic: editedData.vet_clinic,
            vet_phone: editedData.vet_phone,
            vet_address: editedData.vet_address,
            last_visit: editedData.last_visit,
            ...(selectedSection === 'vetInfo' && propertySettings?.general?.requireReapprovalVetInfo === true
              ? { status: 'Pending' }
              : {}),
          }
        : {
            ...dataWithCustomFields,
            ...(currentlyRejected ? { status: 'Pending' } : {}),
          };
      await petService.updatePet(petData.id, dataToSend);
      
      // Fetch fresh pet details from API
      const freshPetData = await petService.getPetDetails(petData.id);
      
      // Update local state with fresh data
      if (freshPetData) {
        // Parse phone number from fresh data
        let phoneNumber = (freshPetData as any).vet_phone || freshPetData.vetPhone || '';
        let countryCode = '+1';
        let numberOnly = phoneNumber;
        
        if (phoneNumber.startsWith('+')) {
          const matchingConfig = countryPhoneConfigs.find(config =>
            phoneNumber.startsWith(config.code)
          );
          if (matchingConfig) {
            countryCode = matchingConfig.code;
            numberOnly = phoneNumber.substring(matchingConfig.code.length);
            numberOnly = numberOnly.replace(/\D/g, '');
          }
        } else {
          numberOnly = phoneNumber.replace(/\D/g, '');
        }
        
        setSelectedVetCountryCode(countryCode);
        setVetPhoneNumber(numberOnly);
        
        const freshPetType = freshPetData.pet_type || 'dog';
        const isCustomFreshPetType = !['dog', 'cat', 'miniature_horse'].includes(freshPetType);
        setCustomPetTypeMode(isCustomFreshPetType);

        const freshBreed = freshPetData.breed || '';
        const freshKnownBreeds = freshPetType === 'dog' ? dogBreeds : freshPetType === 'cat' ? catBreeds : freshPetType === 'miniature_horse' ? miniatureHorseBreeds : [];
        setCustomBreedMode(isCustomFreshPetType || (freshBreed && !freshKnownBreeds.includes(freshBreed)));

        const freshColor = freshPetData.color || '';
        setCustomColorMode(freshColor && !petColors.includes(freshColor));

        const freshMarkings = freshPetData.markings || '';
        setCustomMarkingsMode(freshMarkings && !petMarkings.includes(freshMarkings));

        const freshCustomFieldData: Record<string, string> = {};
        const freshCustomFields = (freshPetData as any).custom_fields || {};
        Object.keys(freshCustomFields).forEach(key => {
          freshCustomFieldData[`custom_${key}`] = freshCustomFields[key] || '';
        });

        setEditedData({
          pet_name: freshPetData.pet_name || '',
          pet_type: freshPetType,
          breed: freshBreed,
          age: freshPetData.age || 0,
          weight: freshPetData.weight || 0,
          gender: freshPetData.gender || '',
          move_in_date: freshPetData.move_in_date,
          move_out_date: freshPetData.move_out_date,
          spayed_neutered: freshPetData.spayed_neutered,
          color: freshColor,
          markings: freshMarkings,
          vet_clinic: (freshPetData as any).vet_clinic || freshPetData.vetClinic || '',
          vet_phone: phoneNumber,
          vet_address: (freshPetData as any).vet_address || freshPetData.vetAddress || '',
          last_visit: (freshPetData as any).last_visit || freshPetData.lastVisit || '',
          pet_role: (freshPetData as any).pet_role || freshPetData.animalType || 'pet',
          esa_professional: (freshPetData as any).esa_professional || freshPetData.esaProfessional || '',
          esa_letter_date: (freshPetData as any).esa_letter_date || freshPetData.esaLetterDate || '',
          esa_registration: (freshPetData as any).esa_registration || freshPetData.esaRegistration || '',
          previous_pet_ownership:
            (freshPetData as any).previous_pet_ownership === true
              ? 'Yes'
              : (freshPetData as any).previous_pet_ownership === false
                ? 'No'
                : '',
          ...freshCustomFieldData,
        });
        
        // Update pet image URL if changed
        if (freshPetData.image_url) {
          setPetImageUrl(freshPetData.image_url);
        }
        
        // Call parent callback with fresh data
        if (onPetUpdated) {
          await onPetUpdated(freshPetData);
        }
      }
      
      setFieldErrors({});
      setIsEditMode(false);
      
      const isApproved = petData.status === 'Active' || petData.status === 'active';
      if (showToast) {
        if (currentlyRejected) {
          showToast('Pet details updated and registration resubmitted for review.', 'success');
        } else if (isApproved && selectedSection === 'vetInfo' && propertySettings?.general?.requireReapprovalVetInfo === true) {
          showToast('Vet info updated. Pet status moved to Pending for re-approval.', 'info');
        } else {
          showToast('Pet details updated successfully!', 'success');
        }
      }
    } catch (error: any) {
      console.error('Error updating pet:', error);
      if (showToast) {
        showToast('Failed to update pet. Please try again.', 'error');
      }
    } finally {
      setIsSaving(false);
      setUploadingPetImage(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      if (!deactivateReason || !deactivateMoveOutDate || !deactivateAcknowledge) return;
      const response = await petService.deactivatePet(petData.id, deactivateReason, deactivateMoveOutDate, deactivateNotes);
      setShowDeactivateConfirm(false);
      setDeactivateReason('');
      setDeactivateMoveOutDate('');
      setDeactivateNotes('');
      setDeactivateAcknowledge(false);
      
      if (showToast) {
        const name = petData.pet_name;
        const reason = (deactivateReason || '').toLowerCase();
        let friendlyMsg = response.message || `${name} has been deactivated successfully.`;
        if (reason.includes('passed away')) {
          friendlyMsg = `We're so sorry for your loss. ${name}'s profile has been gently deactivated.`;
        } else if (reason.includes('ran away')) {
          friendlyMsg = `We hope ${name} finds their way home soon. The profile has been deactivated for now.`;
        }
        showToast(friendlyMsg, 'success');
      }
      
      if (onPetUpdated) {
        const updatedPet = await petService.getPetDetails(petData.id);
        await onPetUpdated(updatedPet);
      }
      onClose();
    } catch (error: any) {
      console.error('Error deactivating pet:', error);
      if (showToast) {
        showToast('Failed to deactivate pet. Please try again.', 'error');
      } else {
        alert('Failed to deactivate pet. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    // Reset phone states
    let phoneNumber = (petData as any).vet_phone || petData.vetPhone || '';
    let countryCode = '+1';
    let numberOnly = phoneNumber;
    
    if (phoneNumber.startsWith('+')) {
      const matchingConfig = countryPhoneConfigs.find(config =>
        phoneNumber.startsWith(config.code)
      );
      if (matchingConfig) {
        countryCode = matchingConfig.code;
        numberOnly = phoneNumber.substring(matchingConfig.code.length);
        numberOnly = numberOnly.replace(/\D/g, '');
      }
    } else {
      numberOnly = phoneNumber.replace(/\D/g, '');
    }
    
    setSelectedVetCountryCode(countryCode);
    setVetPhoneNumber(numberOnly);
    setVetPhoneValidationError('');

    // Reset pending photos
    setPendingPhotos([
      { file: null, preview: null, type: 'front', label: 'Pet Photo' }
    ]);

    const cancelPetType = petData.pet_type || 'dog';
    const isCustomCancelPetType = !['dog', 'cat', 'miniature_horse'].includes(cancelPetType);
    setCustomPetTypeMode(isCustomCancelPetType);

    const cancelBreed = petData.breed || '';
    const cancelKnownBreeds = cancelPetType === 'dog' ? dogBreeds : cancelPetType === 'cat' ? catBreeds : cancelPetType === 'miniature_horse' ? miniatureHorseBreeds : [];
    setCustomBreedMode(isCustomCancelPetType || (cancelBreed && !cancelKnownBreeds.includes(cancelBreed)));

    const cancelColor = petData.color || '';
    setCustomColorMode(cancelColor && !petColors.includes(cancelColor));

    const cancelMarkings = petData.markings || '';
    setCustomMarkingsMode(cancelMarkings && !petMarkings.includes(cancelMarkings));

    const cancelCustomFieldData: Record<string, string> = {};
    const cancelCustomFields = (petData as any).custom_fields || {};
    Object.keys(cancelCustomFields).forEach(key => {
      cancelCustomFieldData[`custom_${key}`] = cancelCustomFields[key] || '';
    });

    setEditedData({
      pet_name: petData.pet_name || '',
      pet_type: petData.pet_type || 'dog',
      breed: cancelBreed,
      age: petData.age || 0,
      weight: petData.weight || 0,
      gender: petData.gender || '',
      move_in_date: petData.move_in_date,
      move_out_date: petData.move_out_date,
      spayed_neutered: petData.spayed_neutered,
      color: cancelColor,
      markings: cancelMarkings,
      vet_clinic: petData.vet_clinic || '',
      vet_phone: petData.vet_phone || '',
      vet_address: petData.vet_address || '',
      last_visit: petData.last_visit || '',
      pet_role: petData.pet_role || 'pet',
      esa_professional: petData.esa_professional || '',
      esa_letter_date: petData.esa_letter_date || '',
      esa_registration: petData.esa_registration || '',
      previous_pet_ownership:
        (petData as any).previous_pet_ownership === true
          ? 'Yes'
          : (petData as any).previous_pet_ownership === false
            ? 'No'
            : '',
      ...cancelCustomFieldData,
    });
    setFieldErrors({});
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    setIsEditMode(false);
  };

  const handleClose = () => {
    if (isEditMode) {
      handleCancel();
    }
    setDocumentsModified(false);
    setModifiedDocTypes(new Set());
    setPendingDocFiles({});
    setSelectedSection('petInfo');
    onClose();
  };


  const handleDocumentUpload = async (event: any, docType: string, docName: string) => {
    const files = Array.from(event.target.files) as File[];
    if (files.length === 0) return;

    const file = files[0];

    const maxSize = 10 * 1024 * 1024;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

    if (file.size > maxSize) {
      if (showToast) {
        showToast('File size must be less than 10MB', 'error');
      }
      event.target.value = '';
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      if (showToast) {
        showToast('Only PDF, JPG, and PNG files are allowed', 'error');
      }
      event.target.value = '';
      return;
    }

    // Compute SHA-256 fingerprint for content-based duplicate detection
    const fileHash = await computeFileHash(file);

    // Cross-type duplicate check using content hash (authoritative) with name+size fallback
    const isDuplicateInPending = Object.entries(pendingDocFiles).some(
      ([existingDocType, cached]) =>
        existingDocType !== docType &&
        (cached.hash
          ? cached.hash === fileHash
          : cached.file.name === file.name && cached.file.size === file.size)
    );
    const isDuplicateInExisting = documents.some(doc => {
      const existingDocType = doc.type || doc.document_type || (doc as any).docType;
      if (!existingDocType || existingDocType === docType) return false;
      if ((doc as any).content_hash) {
        return (doc as any).content_hash === fileHash;
      }
      return (doc as any).original_filename === file.name && doc.size === file.size;
    });

    if (isDuplicateInPending || isDuplicateInExisting) {
      if (showToast) {
        showToast('You cannot upload the same document to a different category. Please choose a different file or upload under the original category.', 'error');
      }
      event.target.value = '';
      return;
    }

    // Cache locally with hash — no S3 upload until Save
    setPendingDocFiles(prev => ({ ...prev, [docType]: { file, docName, hash: fileHash } }));
    setDocumentsModified(true);
    setModifiedDocTypes(prev => new Set(prev).add(docType));
    event.target.value = '';

    if (showToast) {
      showToast(`${docName} selected. Click Save to upload.`, 'success');
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    try {
      await petService.deleteDocument(documentToDelete.id);
      await fetchDocuments();
      setDocumentToDelete(null);
      if (showToast) {
        showToast('Document deleted successfully!', 'success');
      }
    } catch (error: any) {
      console.error('Error deleting document:', error);
      if (showToast) {
        showToast('Failed to delete document. Please try again.', 'error');
      }
    }
  };

  const openPreview = async (doc) => {
    try {
      setPreviewLoadingId(doc.id);
      
      if (doc.s3_url) {
        setPreviewDoc(doc);
        setShowPreview(true);
      } else if (doc.s3_bucket && doc.s3_key) {
        const region = doc.s3_region || 'us-east-1';
        const documentUrl = region === 'us-east-1' 
          ? `https://${doc.s3_bucket}.s3.amazonaws.com/${doc.s3_key}`
          : `https://${doc.s3_bucket}.s3.${region}.amazonaws.com/${doc.s3_key}`;
        
        setPreviewDoc({ ...doc, s3_url: documentUrl });
        setShowPreview(true);
      } else {
        if (showToast) {
          showToast('Preview link unavailable. Missing document URL.', 'error');
        }
      }
    } catch (error: any) {
      console.error('Error opening preview:', error);
      if (showToast) {
        showToast('Failed to open preview. Please try again.', 'error');
      }
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewDoc(null);
  };

  const renderField = (label, value, field, type = 'text', questionId?: string, forceOptional?: boolean) => {
    const isRequired = !forceOptional && (questionId ? isFieldRequired(questionId) : ['pet_name', 'breed', 'weight', 'vet_clinic', 'age', 'gender', 'color'].includes(field));
    // Fields whose only valid "empty" state is null / undefined / "".
    // ``age`` was already special-cased so a 0-year-old pet isn't
    // flagged as missing; ``spayed_neutered`` needs the same
    // treatment because selecting "No" maps to boolean ``false`` and
    // the default ``!editedData[field]`` check below misreads that
    // as "empty", surfacing "This field is required" even though the
    // user clearly picked an answer.
    const allowsFalsyValue = new Set(['age', 'spayed_neutered']);
    const hasError = isEditMode && (
      !!fieldErrors[field] || (isRequired && (
        allowsFalsyValue.has(field)
          ? (editedData[field] === null || editedData[field] === undefined || editedData[field] === '')
          : (!editedData[field] || editedData[field] === '' || editedData[field] === 0)
      ))
    );
    
    if (isEditMode && field) {
      if (type === 'select') {
        const petRole = editedData.pet_role || petData?.pet_role || 'pet';
        const isSA = petRole === 'sa';
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label} {isRequired && <span className="text-red-500">*</span>}</label>
            {customPetTypeMode ? (
              <div className="space-y-1.5 pet-type-autocomplete-container relative">
                <input
                  type="text"
                  value={editedData[field] || ''}
                  onChange={(e) => handlePetTypeSearch(e.target.value)}
                  onFocus={() => {
                    const val = editedData[field] || '';
                    if (val.trim()) {
                      const lower = val.toLowerCase();
                      const filtered = otherPetTypes.filter(t => t.toLowerCase().includes(lower));
                      setPetTypeSuggestions(filtered);
                      setShowPetTypeSuggestions(filtered.length > 0);
                    } else {
                      setPetTypeSuggestions(otherPetTypes);
                      setShowPetTypeSuggestions(true);
                    }
                  }}
                  placeholder="Search or type pet type (e.g. Bird, Rabbit, Turtle...)"
                  required
                  className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                    !editedData[field]?.trim() ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {showPetTypeSuggestions && petTypeSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {petTypeSuggestions.map((type, index) => (
                      <button
                        key={index}
                        type="button"
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm text-gray-700 transition-colors border-b border-gray-50 last:border-b-0"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handlePetTypeSelection(type)}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setCustomPetTypeMode(false);
                    setShowPetTypeSuggestions(false);
                    handleInputChange(field, 'dog');
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  &larr; Back to list
                </button>
              </div>
            ) : (
              <select
                value={editedData[field] || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'other') {
                    setCustomPetTypeMode(true);
                    handleInputChange(field, '');
                  } else {
                    handleInputChange(field, val);
                  }
                }}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]"
              >
                <option value="dog">Dog</option>
                {isSA && <option value="miniature_horse">Miniature Horse</option>}
                {!isSA && <option value="cat">Cat</option>}
                {!isSA && <option value="miniature_horse">Miniature Horse</option>}
                {!isSA && <option value="other">Other</option>}
              </select>
            )}
          </div>
        );
      } else if (type === 'breed-select') {
        const petType = editedData.pet_type || 'dog';
        const breedOptions = petType === 'dog' ? dogBreeds : petType === 'cat' ? catBreeds : petType === 'miniature_horse' ? miniatureHorseBreeds : [];
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            {customBreedMode ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={editedData[field] || ''}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  placeholder="Enter breed name"
                  className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                    hasError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {breedOptions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setCustomBreedMode(false);
                      handleInputChange(field, '');
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    &larr; Back to list
                  </button>
                )}
              </div>
            ) : (
              <select
                value={editedData[field] || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'Other') {
                    setCustomBreedMode(true);
                    handleInputChange(field, '');
                  } else {
                    handleInputChange(field, val);
                  }
                }}
                className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                  hasError ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select breed</option>
                {breedOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            )}
            {hasError && (
              <p className="text-xs text-red-600 mt-1">This field is required</p>
            )}
          </div>
        );
      } else if (type === 'color-select') {
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            {customColorMode ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={editedData[field] || ''}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  placeholder="Enter color"
                  className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                    hasError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => {
                    setCustomColorMode(false);
                    handleInputChange(field, '');
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  &larr; Back to list
                </button>
              </div>
            ) : (
              <select
                value={editedData[field] || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'Other') {
                    setCustomColorMode(true);
                    handleInputChange(field, '');
                  } else {
                    handleInputChange(field, val);
                  }
                }}
                className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                  hasError ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select color</option>
                {petColors.map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
            )}
            {hasError && (
              <p className="text-xs text-red-600 mt-1">This field is required</p>
            )}
          </div>
        );
      } else if (type === 'markings-select') {
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            {customMarkingsMode ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={editedData[field] || ''}
                  onChange={(e) => handleInputChange(field, e.target.value)}
                  placeholder="Enter markings"
                  className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                    hasError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => {
                    setCustomMarkingsMode(false);
                    handleInputChange(field, '');
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  &larr; Back to list
                </button>
              </div>
            ) : (
              <select
                value={editedData[field] || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'Other') {
                    setCustomMarkingsMode(true);
                    handleInputChange(field, '');
                  } else {
                    handleInputChange(field, val);
                  }
                }}
                className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                  hasError ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{isRequired ? 'Select markings' : 'Select markings (optional)'}</option>
                {petMarkings.map(marking => (
                  <option key={marking} value={marking}>{marking}</option>
                ))}
              </select>
            )}
            {hasError && (
              <p className="text-xs text-red-600 mt-1">This field is required</p>
            )}
          </div>
        );
      } else if (type === 'gender-select') {
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <select
              value={editedData[field] || ''}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                hasError ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            {hasError && (
              <p className="text-xs text-red-600 mt-1">This field is required</p>
            )}
          </div>
        );
      } else if (type === 'spayed-select') {
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <select
              value={editedData[field] !== undefined ? String(editedData[field]) : ''}
              onChange={(e) => handleInputChange(field, e.target.value === 'true')}
              className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                hasError ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select status</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
            {hasError && (
              <p className="text-xs text-red-600 mt-1">This field is required</p>
            )}
          </div>
        );
      } else if (type === 'age-select') {
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <select
              value={editedData[field] !== undefined ? String(editedData[field]) : ''}
              onChange={(e) => handleInputChange(field, parseInt(e.target.value))}
              className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                hasError ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select age</option>
              <option value="0">Less than a year</option>
              {Array.from({ length: 25 }, (_, i) => i + 1).map(year => (
                <option key={year} value={year}>
                  {year} {year === 1 ? 'year' : 'years'}
                </option>
              ))}
            </select>
            {hasError && (
              <p className="text-xs text-red-600 mt-1">This field is required</p>
            )}
          </div>
        );
      } else if (type === 'checkbox') {
        return (
          <div>
            <label className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <input
                type="checkbox"
                checked={editedData[field] || false}
                onChange={(e) => handleInputChange(field, e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-[color:var(--pmc-primary)]"
              />
              {label}
            </label>
          </div>
        );
      } else if (type === 'date') {
        // last_visit (q16) is restricted to "within the past year, not in
        // the future" -- mirrors the wizard's date input in
        // QuestionnaireModel.tsx (line ~3939). Other date fields
        // (move_in_date, esa_letter_date, ...) keep their existing
        // free-range behaviour.
        const isLastVisit = field === 'last_visit';
        const todayStr = isLastVisit ? getTodayDate() : undefined;
        const oneYearAgoStr = isLastVisit ? getOneYearAgoDate() : undefined;
        const currentValue = editedData[field]
          ? new Date(editedData[field]).toISOString().split('T')[0]
          : '';
        const isOutOfRange =
          isLastVisit &&
          !!currentValue &&
          (currentValue > (todayStr || '') || currentValue < (oneYearAgoStr || ''));
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <input
              type="date"
              value={currentValue}
              min={oneYearAgoStr}
              max={todayStr}
              onChange={(e) => {
                const v = e.target.value;
                if (isLastVisit && v && (v > (todayStr || '') || v < (oneYearAgoStr || ''))) {
                  // Reject out-of-range pick (typed via keyboard) so the
                  // input stays anchored to the previous valid value.
                  return;
                }
                handleInputChange(field, v);
              }}
              className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                hasError || isOutOfRange ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {hasError && (
              <p className="text-xs text-red-600 mt-1">This field is required</p>
            )}
            {isOutOfRange && !hasError && (
              <p className="text-xs text-red-600 mt-1">
                Date must be within the past year (not in the future).
              </p>
            )}
          </div>
        );
      } else {
        return (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <input
              type={type}
              value={editedData[field] || ''}
              maxLength={field === 'pet_name' ? 20 : undefined}
              onChange={(e) => {
                if (type === 'number') {
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value)) {
                    // Round to nearest 0.5
                    const rounded = Math.round(value * 2) / 2;
                    handleInputChange(field, rounded);
                  } else if (e.target.value === '') {
                    handleInputChange(field, 0);
                  }
                } else if (field === 'pet_name') {
                  const validChars = /^[a-zA-Z\s\-']*$/;
                  if (validChars.test(e.target.value)) {
                    handleInputChange(field, e.target.value);
                  }
                } else {
                  handleInputChange(field, e.target.value);
                }
              }}
              onBlur={(e) => {
                if (type === 'number') {
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value)) {
                    const rounded = Math.round(value * 2) / 2;
                    handleInputChange(field, rounded);
                  }
                }
              }}
              step={type === 'number' ? '0.5' : undefined}
              min={type === 'number' ? '0' : undefined}
              className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                hasError ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {hasError && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors[field] || 'This field is required'}</p>
            )}
          </div>
        );
      }
    }

    return (
      <div>
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</label>
        <p className="text-sm font-semibold text-gray-900 mt-1">{value}</p>
      </div>
    );
  };

  const navigationItems = [
    { id: 'petInfo', label: 'Pet Information', active: selectedSection === 'petInfo' },
    { id: 'esaInfo', label: 'Animal Type Information', active: selectedSection === 'esaInfo' },
    { id: 'vetInfo', label: 'Veterinary Information', active: selectedSection === 'vetInfo' },
    { id: 'propertyInfo', label: 'Property Information', active: selectedSection === 'propertyInfo' },
    { id: 'documents', label: 'Documents', active: selectedSection === 'documents' },
    { id: 'registrationDetails', label: 'Registration Details', active: selectedSection === 'registrationDetails' }
  ];

  const renderSectionContent = () => {
    switch (selectedSection) {
      case 'petInfo':
        // Determine which photo to display (use first photo with content)
        const displayPhotoUrl = pendingPhotos.find(p => p.preview)?.preview || petImageUrl;

        return (
          <div className="space-y-6">
            {/* Locked notice when pet info is locked (approved or ESA/SA submitted) */}
            {isPetInfoLocked && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Pet Information Locked</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {petData?.status === 'Active'
                      ? 'This pet\'s registration has been approved. Pet information cannot be modified. You can still update veterinary information and upload new documents.'
                      : 'This pet\'s registration has been submitted and is pending review. Pet information cannot be modified. You can still update veterinary information and upload new documents.'}
                  </p>
                </div>
              </div>
            )}
            {!isEditMode && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {isFieldVisible('q1') && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{getFieldLabel('q1', 'Photo')}</label>
                  <div className="mt-2 flex items-center gap-4">
                    <div
                      className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0 cursor-pointer hover:border-purple-500 transition-colors relative group"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (displayPhotoUrl) {
                          openCarousel();
                        }
                      }}
                    >
                      {displayPhotoUrl ? (
                        <>
                          <img src={displayPhotoUrl} alt={petData.pet_name || 'Pet'} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                            <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">No photo</span>
                      )}
                    </div>
                  </div>
                </div>
                )}
                {isFieldVisible('q3') && renderField(getFieldLabel('q3', 'Name'), petData.pet_name || 'Not provided', 'pet_name', 'text', 'q3')}
                {isFieldVisible('q2') && renderField(getFieldLabel('q2', 'Type'), petData.pet_type ? (petData.pet_type === 'miniature_horse' ? 'Miniature Horse' : petData.pet_type.charAt(0).toUpperCase() + petData.pet_type.slice(1)) : 'Not provided', 'pet_type', 'select', 'q2')}
              </div>
            )}

            {isEditMode && isFieldVisible('q1') && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                    {getFieldLabel('q1', 'Pet Photos')} {isFieldRequired('q1') ? '(at least 1 recommended)' : '(optional)'}
                  </label>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-28 h-28 rounded-lg border border-gray-300 overflow-hidden bg-gray-50 flex items-center justify-center">
                        {loadingExistingPhotos ? (
                          <LoadingSpinner size="xs" text="" />
                        ) : pendingPhotos[0]?.preview ? (
                          <img src={pendingPhotos[0].preview} alt="Pet photo preview" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs text-gray-400">No photo</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <label className={`inline-flex items-center shadow-[0px_1px_2px_rgba(0,0,0,0.05)] rounded-[1000px] bg-white border border-[#b9bbc6] h-9 overflow-hidden px-[12px] py-2 text-[#1e1f24] transition-colors ${loadingExistingPhotos ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={loadingExistingPhotos}
                          onChange={(e) => {
                            const file = e.target.files && e.target.files[0];
                            if (!file) return;

                            // Capture the old preview BEFORE setState, but defer
                            // the revoke until React has committed + painted the
                            // new src. Revoking synchronously invalidates the
                            // blob: URL still mounted on <img> and the browser
                            // can't fall back to "keep showing old image while
                            // new loads" — for one or two frames the slot drops
                            // to a broken-image / empty state, which is the
                            // pop-out glitch users hit when replacing a photo.
                            // Only blob: URLs need revoking; S3 presigned URLs
                            // (initial-load preview from fetchExistingPhotos)
                            // are not blobs and would be a no-op anyway.
                            const oldPreview = pendingPhotos[0]?.preview;
                            const preview = URL.createObjectURL(file);
                            const updatedPhotos = [...pendingPhotos];
                            updatedPhotos[0] = { ...pendingPhotos[0], file, preview };
                            setPendingPhotos(updatedPhotos);

                            if (oldPreview && oldPreview.startsWith('blob:')) {
                              requestAnimationFrame(() => {
                                requestAnimationFrame(() => URL.revokeObjectURL(oldPreview));
                              });
                            }

                            e.target.value = '';
                          }}
                        />
                        <span className="text-xs uppercase font-medium">Upload Photo</span>
                      </label>
                      {pendingPhotos[0]?.preview && (
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setPreviewPhotoIndex(0);
                              setShowPhotoPreview(true);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            Preview
                          </button>
                          {pendingPhotos[0]?.file && (
                            <button
                              type="button"
                              onClick={() => {
                                if (pendingPhotos[0]?.preview) {
                                  URL.revokeObjectURL(pendingPhotos[0].preview);
                                }
                                const updatedPhotos = [...pendingPhotos];
                                updatedPhotos[0] = { ...pendingPhotos[0], file: null, preview: null };
                                setPendingPhotos(updatedPhotos);
                              }}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {pendingPhotos.some(p => p.file) && (
                    <p className="text-xs text-green-600 mt-2">Photos ready to save</p>
                  )}

                  {showPhotoPreview && pendingPhotos[previewPhotoIndex]?.preview && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => setShowPhotoPreview(false)}>
                      <div className="relative bg-white rounded-lg shadow-xl p-3 max-w-md max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => setShowPhotoPreview(false)}
                          className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-md text-gray-500 hover:text-gray-700 text-sm font-bold"
                        >
                          ✕
                        </button>
                        <img
                          src={pendingPhotos[previewPhotoIndex].preview}
                          alt="Pet photo preview"
                          className="max-w-full max-h-[70vh] rounded object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {isFieldVisible('q3') && renderField(getFieldLabel('q3', 'Name'), petData.pet_name || 'Not provided', 'pet_name', 'text', 'q3')}
                  {isFieldVisible('q2') && renderField(getFieldLabel('q2', 'Type'), petData.pet_type ? (petData.pet_type === 'miniature_horse' ? 'Miniature Horse' : petData.pet_type.charAt(0).toUpperCase() + petData.pet_type.slice(1)) : 'Not provided', 'pet_type', 'select', 'q2')}
                </div>
              </div>
            )}

            {isEditMode && !isFieldVisible('q1') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {isFieldVisible('q3') && renderField(getFieldLabel('q3', 'Name'), petData.pet_name || 'Not provided', 'pet_name', 'text', 'q3')}
                {isFieldVisible('q2') && renderField(getFieldLabel('q2', 'Type'), petData.pet_type ? (petData.pet_type === 'miniature_horse' ? 'Miniature Horse' : petData.pet_type.charAt(0).toUpperCase() + petData.pet_type.slice(1)) : 'Not provided', 'pet_type', 'select', 'q2')}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {isFieldVisible('q4') && renderField(getFieldLabel('q4', 'Breed'), petData.breed || 'Not provided', 'breed', 'breed-select', 'q4')}
              {isFieldVisible('q6') && renderField(getFieldLabel('q6', 'Age'), petData.age !== undefined && petData.age !== null ? (petData.age === 0 ? 'Less than a year' : `${petData.age} ${petData.age === 1 ? 'year' : 'years'}`) : 'Not provided', 'age', 'age-select', 'q6')}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {isFieldVisible('q5') && renderField(getFieldLabel('q5', 'Weight') + ' (lbs)', petData.weight ? `${petData.weight} lbs` : 'Not provided', 'weight', 'number', 'q5')}
              {isFieldVisible('q11') && renderField(getFieldLabel('q11', 'Color'), petData.color || 'Not provided', 'color', 'color-select', 'q11')}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {isFieldVisible('q7') && renderField(getFieldLabel('q7', 'Gender'), petData.gender ? petData.gender : 'Not provided', 'gender', 'gender-select', 'q7')}
              {isFieldVisible('q8') && renderField(getFieldLabel('q8', 'Spayed / Neutered'), petData.spayed_neutered === true ? 'Yes' : petData.spayed_neutered === false ? 'No' : 'Not provided', 'spayed_neutered', 'spayed-select', 'q8')}
            </div>
            
            {isFieldVisible('q12') && (
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              {renderField(getFieldLabel('q12', 'Markings/Patterns'), petData.markings || 'Not provided', 'markings', 'markings-select', 'q12')}
            </div>
            )}

            {/* Move-in/Move-out dates moved to the propertyInfo section as read-only display */}

            {isFieldVisible('q19') && (
              isEditMode ? (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {getFieldLabel('q19', 'Previous Pet Ownership History')} {isFieldRequired('q19') && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={editedData.previous_pet_ownership || ''}
                    onChange={(e) => handleInputChange('previous_pet_ownership', e.target.value)}
                    className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                      fieldErrors.previous_pet_ownership ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {fieldErrors.previous_pet_ownership && <p className="text-xs text-red-600 mt-1">{fieldErrors.previous_pet_ownership}</p>}
                </div>
              ) : (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{getFieldLabel('q19', 'Previous Pet Ownership History')}</label>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{petDataAny.previous_pet_ownership === true ? 'Yes' : petDataAny.previous_pet_ownership === false ? 'No' : 'Not provided'}</p>
                </div>
              )
            )}

            {/* Custom admin-added questions for animal_info */}
            {getCustomQuestions('animal_info').length > 0 && (
              <div className="space-y-4">
                {getCustomQuestions('animal_info').map((q: any) => {
                  const fieldKey = `custom_${q.id}`;
                  const value = editedData[fieldKey] || petDataAny?.custom_fields?.[q.id] || '';
                  const hasErr = !!fieldErrors[fieldKey];
                  const errClass = hasErr ? 'border-red-500' : 'border-gray-300';
                  return isEditMode ? (
                    <div key={q.id}>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {q.question} {q.required && <span className="text-red-500">*</span>}
                      </label>
                      {q.type === 'select' && q.options?.length > 0 ? (
                        <select
                          value={editedData[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          className={`mt-1 w-full px-3 py-2 border ${errClass} rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]`}
                        >
                          <option value="">Select</option>
                          {q.options.map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : q.type === 'date' ? (
                        <input
                          type="date"
                          value={editedData[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          className={`mt-1 w-full px-3 py-2 border ${errClass} rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]`}
                        />
                      ) : q.type === 'number' ? (
                        <input
                          type="number"
                          value={editedData[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={`Enter ${q.question.toLowerCase()}`}
                          className={`mt-1 w-full px-3 py-2 border ${errClass} rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={editedData[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={`Enter ${q.question.toLowerCase()}`}
                          className={`mt-1 w-full px-3 py-2 border ${errClass} rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]`}
                        />
                      )}
                      {hasErr && <p className="text-xs text-red-600 mt-1">{fieldErrors[fieldKey]}</p>}
                    </div>
                  ) : (
                    <div key={q.id}>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{q.question}</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">{value || 'Not provided'}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'esaInfo':
        return (
          <div className="space-y-6">
            {/* Locked notice when pet info is locked (approved or ESA/SA submitted) */}
            {isPetInfoLocked && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Animal Type Information Locked</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {petData?.status === 'Active'
                      ? 'This pet\'s registration has been approved. Animal type information cannot be modified.'
                      : 'This registration has been submitted and is pending review. Animal type information cannot be modified.'}
                  </p>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Animal Type</label>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {petData.pet_role === 'esa' ? 'Emotional Support Animal' : petData.pet_role === 'sa' ? 'Service Animal' : 'Pet'}
              </p>
            </div>
            {(petData.pet_role === 'esa' || petData.pet_role === 'sa') ? (
              <>
                {petData?.status?.toLowerCase() !== 'inactive' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Info className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-orange-900 mb-1">{petData.pet_role === 'sa' ? 'Service Animal Documentation' : 'Emotional Support Animal Documentation'}</h4>
                        <p className="text-sm text-orange-800">Pending Approval.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{petData.pet_role === 'sa' ? 'Service Animal Professional' : 'Emotional Support Animal Professional'}</label>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{petData.esa_professional || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Registration Number</label>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{petData.esa_registration || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Letter Date</label>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{formatDate(petData.esa_letter_date) || 'Not provided'}</p>
                </div>
              </>
            ) : null}
          </div>
        );

      case 'vetInfo':
        const currentVetPhoneConfig = countryPhoneConfigs.find(c => c.code === selectedVetCountryCode) || countryPhoneConfigs[0];
        const isServiceAnimal = petData?.pet_role === 'sa';

        return (
          <div className="space-y-6">
            {isEditMode && isServiceAnimal && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900">
                  Veterinary information is optional for Service Animals. Property managers may choose to collect it, but it is not required by law.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {isFieldVisible('q13') && renderField(getFieldLabel('q13', 'Clinic Name'), petData.vet_clinic || 'Not provided', 'vet_clinic', 'text', 'q13', isServiceAnimal)}

              {isFieldVisible('q15') && (
              isEditMode ? (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {getFieldLabel('q15', 'Phone')} {!isServiceAnimal && isFieldRequired('q15') && <span className="text-red-500">*</span>}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <div className="relative w-full sm:w-auto">
                      <select
                        className="w-full sm:w-auto appearance-none px-3 py-2 pr-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-[color:var(--pmc-primary)] cursor-pointer text-sm font-semibold text-gray-900"
                        value={selectedVetCountryCode}
                        onChange={(e) => handleVetCountryCodeChange(e.target.value)}
                      >
                        {countryPhoneConfigs.map(config => (
                          <option key={config.code} value={config.code}>
                            {config.flag} {config.code}
                          </option>
                        ))}
                      </select>
                      <Globe className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                    
                    <div className="flex-1 relative">
                      <input
                        type="tel"
                        placeholder={`Enter ${currentVetPhoneConfig.maxDigits} digits`}
                        className={`w-full px-3 py-2 pr-16 border rounded-md focus:ring-2 focus:ring-[color:var(--pmc-primary)] text-sm font-semibold text-gray-900 ${
                          vetPhoneValidationError ? 'border-red-500' : 
                          vetPhoneNumber.length === currentVetPhoneConfig.maxDigits ? 'border-green-500 bg-green-50' :
                          'border-gray-300'
                        }`}
                        value={vetPhoneNumber}
                        onChange={(e) => handleVetPhoneNumberChange(e.target.value)}
                        onBlur={validateVetPhoneNumber}
                        maxLength={currentVetPhoneConfig.maxDigits}
                      />
                      <span className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-xs ${
                        vetPhoneNumber.length === currentVetPhoneConfig.maxDigits ? 'text-green-600' :
                        vetPhoneNumber.length >= currentVetPhoneConfig.maxDigits * 0.8 ? 'text-orange-500' :
                        'text-gray-400'
                      }`}>
                        {vetPhoneNumber.length}/{currentVetPhoneConfig.maxDigits}
                      </span>
                    </div>
                  </div>
                  {vetPhoneValidationError && (
                    <p className="text-xs text-red-600 mt-1 animate-pulse">
                      {vetPhoneValidationError}
                    </p>
                  )}
                </div>
              ) : (
                renderField(getFieldLabel('q15', 'Phone'), formatPhoneNumber(petData.vet_phone), 'vet_phone', 'tel', 'q15', isServiceAnimal)
              )
              )}
            </div>
            
            {isFieldVisible('q14') && (
              isEditMode ? (
                <div className="relative address-autocomplete-container">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {getFieldLabel('q14', 'Address')} {!isServiceAnimal && isFieldRequired('q14') && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Start typing clinic address..."
                      value={editedData.vet_address || ''}
                      onChange={(e) => {
                        hasUserTypedAddress.current = true;
                        handleInputChange('vet_address', e.target.value);
                        if (!e.target.value) setShowAddressSuggestions(false);
                      }}
                      onFocus={() => { if (addressSuggestions.length > 0) setShowAddressSuggestions(true); }}
                      className={`mt-1 w-full px-3 py-2 border rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)] ${
                        fieldErrors.vet_address ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {addressSearchLoading && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                  {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {addressSuggestions.map((suggestion, index) => (
                        <div
                          key={`${suggestion.source}-${index}`}
                          className="p-3 hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                          onClick={() => handleAddressSelection(suggestion)}
                        >
                          <div className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm text-gray-900">{suggestion.display}</p>
                              <p className="text-xs text-gray-500 mt-0.5 capitalize">{suggestion.source}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {fieldErrors.vet_address && <p className="text-xs text-red-600 mt-1">{fieldErrors.vet_address}</p>}
                </div>
              ) : (
                renderField(getFieldLabel('q14', 'Address'), petData.vet_address || 'Not provided', 'vet_address', 'text', 'q14', isServiceAnimal)
              )
            )}
            {isFieldVisible('q16') && renderField(getFieldLabel('q16', 'Last Visit'), formatDate(petData.last_visit), 'last_visit', 'date', 'q16', isServiceAnimal)}

            {/* Custom admin-added questions for vet_info */}
            {getCustomQuestions('vet_info').length > 0 && (
              <div className="space-y-4">
                {getCustomQuestions('vet_info').map((q: any) => {
                  const fieldKey = `custom_${q.id}`;
                  const value = editedData[fieldKey] || petDataAny?.custom_fields?.[q.id] || '';
                  const hasErr = !!fieldErrors[fieldKey];
                  const errClass = hasErr ? 'border-red-500' : 'border-gray-300';
                  return isEditMode ? (
                    <div key={q.id}>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {q.question} {q.required && <span className="text-red-500">*</span>}
                      </label>
                      {q.type === 'select' && q.options?.length > 0 ? (
                        <select
                          value={editedData[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          className={`mt-1 w-full px-3 py-2 border ${errClass} rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]`}
                        >
                          <option value="">Select</option>
                          {q.options.map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : q.type === 'date' ? (
                        <input
                          type="date"
                          value={editedData[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          className={`mt-1 w-full px-3 py-2 border ${errClass} rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]`}
                        />
                      ) : q.type === 'number' ? (
                        <input
                          type="number"
                          value={editedData[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={`Enter ${q.question.toLowerCase()}`}
                          className={`mt-1 w-full px-3 py-2 border ${errClass} rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]`}
                        />
                      ) : (
                        <input
                          type="text"
                          value={editedData[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={`Enter ${q.question.toLowerCase()}`}
                          className={`mt-1 w-full px-3 py-2 border ${errClass} rounded-md text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]`}
                        />
                      )}
                      {hasErr && <p className="text-xs text-red-600 mt-1">{fieldErrors[fieldKey]}</p>}
                    </div>
                  ) : (
                    <div key={q.id}>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{q.question}</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">{value || 'Not provided'}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case 'propertyInfo':
        return (
          <div className="space-y-6">
            {/* Locked notice when pet info is locked (approved or ESA/SA submitted) */}
            {isPetInfoLocked && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Property Information Locked</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {petData?.status === 'Active'
                      ? 'This pet\'s registration has been approved. Property information cannot be modified.'
                      : 'This registration has been submitted and is pending review. Property information cannot be modified.'}
                  </p>
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Property</label>
              <p className="text-sm font-semibold text-gray-900 mt-1">{petData.property || 'Not provided'}</p>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Unit</label>
              <p className="text-sm font-semibold text-gray-900 mt-1">{petData.unit || 'Not provided'}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Move-In Date</label>
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {formatDate(petDataAny.move_in_date) || 'Not provided'}
                </p>
              </div>
              {petDataAny.move_out_date && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Move-Out Date</label>
                  <p className="text-sm font-semibold text-gray-900 mt-1">
                    {formatDate(petDataAny.move_out_date)}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 'registrationDetails': {
        const rawStatusHistory = petDataAny.status_history || [];
        const statusHistory = rawStatusHistory.filter((entry: any) => {
          if (entry.actor_type === 'gc' || entry.actor_type === 'gc_agent') return false;
          if (entry.note && /^GC Recommend/i.test(entry.note)) return false;
          return true;
        });
        const resubmissionCount = petDataAny.resubmission_count || 0;
        const rejectionReasons = petDataAny.rejection_reasons || [];
        const isRejected = petData.status?.toLowerCase() === 'rejected';
        const latestRejection = [...statusHistory].reverse().find((h: any) => h.status === 'Rejected');
        const previousRejections = statusHistory.filter((h: any) => h.status === 'Rejected');
        const hasDetailedHistory = statusHistory.length > 0;

        const petStatus = petData.status || 'Pending';
        const paymentStatus = petDataAny.stripe_payment_status || 'pending';

        const progressSteps = (() => {
          const steps = [
            { label: 'Registered', key: 'registered' },
            { label: 'Payment', key: 'payment' },
            { label: 'Under Review', key: 'review' },
            { label: 'Decision', key: 'decision' },
          ];

          let currentStep = 0;
          let stepStatuses: ('completed' | 'current' | 'upcoming' | 'rejected')[] = ['upcoming', 'upcoming', 'upcoming', 'upcoming'];

          if (petStatus === 'Drafted') {
            stepStatuses = ['current', 'upcoming', 'upcoming', 'upcoming'];
            currentStep = 0;
          } else if (petStatus === 'PendingPayment' || (petStatus === 'Pending' && paymentStatus === 'pending' && paymentStatus !== 'exempted')) {
            stepStatuses = ['completed', 'current', 'upcoming', 'upcoming'];
            currentStep = 1;
          } else if (petStatus === 'Pending' && (paymentStatus === 'paid' || paymentStatus === 'exempted')) {
            stepStatuses = ['completed', 'completed', 'current', 'upcoming'];
            currentStep = 2;
          } else if (petStatus === 'Active') {
            stepStatuses = ['completed', 'completed', 'completed', 'completed'];
            currentStep = 3;
            steps[3].label = 'Approved';
          } else if (petStatus === 'Rejected') {
            stepStatuses = ['completed', 'completed', 'completed', 'rejected'];
            currentStep = 3;
            steps[3].label = 'Rejected';
          } else if (petStatus === 'Inactive' || petStatus === 'Expired') {
            stepStatuses = ['completed', 'completed', 'completed', 'completed'];
            currentStep = 3;
            steps[3].label = petStatus === 'Inactive' ? 'Deactivated' : 'Expired';
          } else {
            stepStatuses = ['completed', 'completed', 'current', 'upcoming'];
            currentStep = 2;
          }

          if (paymentStatus === 'exempted') {
            steps[1].label = 'Fee Exempt';
          } else if (paymentStatus === 'paid') {
            steps[1].label = 'Paid';
          } else if (paymentStatus === 'failed') {
            steps[1].label = 'Payment Failed';
          }

          return steps.map((s, i) => ({ ...s, status: stepStatuses[i] }));
        })();

        return (
          <div className="space-y-6">
            {/* Locked notice when pet info is locked (approved or ESA/SA submitted) */}
            {isPetInfoLocked && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Registration Details Locked</p>
                  <p className="text-sm text-amber-700 mt-1">
                    {petData?.status === 'Active'
                      ? 'This pet\'s registration has been approved. Registration details cannot be modified.'
                      : 'This registration has been submitted and is pending review. Registration details cannot be modified.'}
                  </p>
                </div>
              </div>
            )}

            {/* Rejection Action Banner (only when currently rejected) */}
            {isRejected && (() => {
              const parsed = parseRejectionEntry(latestRejection);
              const fallbackReasons = parsed.reasons.length > 0
                ? parsed.reasons
                : resolveRejectionReasons(rejectionReasons);
              const adminNote = parsed.note;
              const primary: RejectionReasonInfo | null = fallbackReasons[0] || null;
              const primaryStep = primary?.actionStep || 'documents';
              const primaryLabel = primary?.actionLabel || 'Update and resubmit';

              return (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-800">Action required — your registration was rejected</h4>
                      {fallbackReasons.length > 0 ? (
                        <ul className="mt-2 space-y-2">
                          {fallbackReasons.map((r, i) => (
                            <li key={`${r.code}-${i}`} className="text-sm">
                              <p className="font-semibold text-red-800">{r.name}</p>
                              <p className="text-red-700">{r.description}</p>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-red-700 mt-1">
                          Rejection details unavailable. Please contact your property manager for more information.
                        </p>
                      )}
                      {adminNote && (
                        <div className="mt-3 p-2 bg-white/60 border border-red-200 rounded">
                          <p className="text-xs font-medium text-red-800 mb-0.5">Note from reviewer</p>
                          <p className="text-sm text-red-700 whitespace-pre-line">{adminNote}</p>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          setSelectedSection(primaryStep);
                        }}
                        className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                      >
                        {primaryLabel}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Status badge + Resubmission count */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                petData.status === 'Active' ? 'bg-green-100 text-green-800' :
                petData.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                petData.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                petData.status === 'Inactive' ? 'bg-gray-100 text-gray-600' :
                'bg-gray-100 text-gray-800'
              }`}>
                {petData.status === 'Active' ? <CheckCircle className="w-3 h-3" /> :
                 petData.status === 'Rejected' ? <XCircle className="w-3 h-3" /> :
                 <Clock className="w-3 h-3" />}
                {petData.status === 'Active' ? 'Approved' : petData.status || 'Pending'}
              </span>
              {resubmissionCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Resubmission #{resubmissionCount}
                </span>
              )}
            </div>

            {/* Progress Stepper — derived from status + payment_status, always shown */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Registration progress</h4>
              <div className="flex items-center w-full">
                {progressSteps.map((step, idx) => (
                  <div key={step.key} className="flex items-center flex-1 last:flex-initial">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                        step.status === 'completed' ? 'bg-green-100 border-green-500' :
                        step.status === 'current' ? 'bg-blue-100 border-[color:var(--pmc-primary)]' :
                        step.status === 'rejected' ? 'bg-red-100 border-red-500' :
                        'bg-gray-100 border-gray-300'
                      }`}>
                        {step.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-600" /> :
                         step.status === 'rejected' ? <XCircle className="w-4 h-4 text-red-600" /> :
                         step.status === 'current' ? <Clock className="w-4 h-4 text-blue-600" /> :
                         <span className="w-2 h-2 rounded-full bg-gray-300" />}
                      </div>
                      <span className={`text-xs mt-1.5 font-medium text-center whitespace-nowrap ${
                        step.status === 'completed' ? 'text-green-700' :
                        step.status === 'current' ? 'text-blue-700' :
                        step.status === 'rejected' ? 'text-red-700' :
                        'text-gray-400'
                      }`}>{step.label}</span>
                    </div>
                    {idx < progressSteps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 mt-[-18px] ${
                        progressSteps[idx + 1].status === 'upcoming' ? 'bg-gray-200' :
                        progressSteps[idx + 1].status === 'rejected' ? 'bg-red-200' :
                        'bg-green-300'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Basic Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Registration Date</label>
                <p className="text-sm font-semibold text-gray-900 mt-1">{formatDate(petData.registered_date)}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Animal Type</label>
                <div className="mt-1">
                  {(petData.pet_role === 'esa' || petData.pet_role === 'sa') ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">
                      {petData.pet_role === 'esa' ? 'Emotional Support Animal' : 'Service Animal'}
                    </span>
                  ) : (
                    <span className="text-sm font-semibold text-gray-900">Pet</span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment</label>
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {paymentStatus === 'paid' ? 'Paid' :
                   paymentStatus === 'exempted' ? 'Fee Exempt' :
                   paymentStatus === 'failed' ? 'Payment Failed' :
                   paymentStatus === 'pending' ? 'Awaiting Payment' : 'N/A'}
                </p>
              </div>
            </div>

            {/* Detailed Status Timeline (from extra_data — only for newer registrations) */}
            {hasDetailedHistory ? (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Registration timeline</h4>
                <div className="relative">
                  {[...statusHistory].reverse().map((entry: any, index: number) => {
                    const statusConfig = {
                      'Approved': { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200' },
                      'Rejected': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-200' },
                      'Pending': { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200' },
                    }[entry.status] || { icon: AlertCircle, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200' };
                    const Icon = statusConfig.icon;
                    const ts = new Date(entry.timestamp);
                    const isResubmission = entry.status === 'Pending' && entry.note?.toLowerCase().includes('resubmit');

                    return (
                      <div key={index} className="flex gap-3 pb-6 last:pb-0 relative">
                        {index < statusHistory.length - 1 && (
                          <div className="absolute left-[13px] top-[28px] w-0.5 h-[calc(100%-16px)] bg-gray-200" />
                        )}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${statusConfig.bg} ${statusConfig.border} border`}>
                          <Icon className={`w-4 h-4 ${statusConfig.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-semibold ${statusConfig.color}`}>
                              {isResubmission ? 'Resubmitted' : entry.status === 'Active' ? 'Approved' : entry.status}
                            </span>
                            <span className="text-xs text-gray-500">
                              {ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </span>
                          </div>
                          {entry.actor && entry.actor_type === 'admin' && (entry.status === 'Approved' || entry.status === 'Active' || entry.status === 'Rejected') && (
                            <p className="text-xs text-gray-500 mt-0.5">by {entry.actor}</p>
                          )}
                          {entry.status === 'Rejected' ? (() => {
                            const parsed = parseRejectionEntry(entry);
                            if (parsed.reasons.length === 0 && !parsed.note) return null;
                            return (
                              <div className="mt-1 space-y-1.5">
                                {parsed.reasons.map((r, i) => (
                                  <div key={`${r.code}-${i}`} className="text-sm">
                                    <p className="font-semibold text-red-700">{r.name}</p>
                                    <p className="text-red-700/90">{r.description}</p>
                                  </div>
                                ))}
                                {parsed.note && (
                                  <p className="text-sm text-gray-700">
                                    <span className="font-medium">Note: </span>{parsed.note}
                                  </p>
                                )}
                              </div>
                            );
                          })() : (
                            <p className="text-sm text-gray-600 mt-0.5">
                              {entry.note || (entry.status === 'Approved' ? 'Your registration has been approved.' :
                                isResubmission ? 'You resubmitted your registration with updated documents.' :
                                entry.status === 'Pending' ? 'You submitted your registration for review.' :
                                '')}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-500">Detailed timeline unavailable for this registration.</p>
              </div>
            )}

            {/* Previous rejections (when currently approved/pending but had past rejections) */}
            {!isRejected && previousRejections.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Previous rejections</h4>
                <div className="space-y-2">
                  {[...previousRejections].reverse().map((entry: any, index: number) => {
                    const parsed = parseRejectionEntry(entry);
                    const ts = new Date(entry.timestamp);
                    const tsLabel = `${ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                    return (
                      <div key={index} className="border-l-4 border-amber-300 bg-amber-50 p-3 rounded-r-lg">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {parsed.reasons.length > 0 ? (
                              <div className="space-y-1.5">
                                {parsed.reasons.map((r, i) => (
                                  <div key={`${r.code}-${i}`}>
                                    <p className="text-sm font-semibold text-amber-900">{r.name}</p>
                                    <p className="text-sm text-amber-800">{r.description}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-amber-800">{parsed.note || 'Details unavailable'}</p>
                            )}
                            {parsed.note && parsed.reasons.length > 0 && (
                              <p className="text-sm text-amber-800 mt-1">
                                <span className="font-medium">Note: </span>{parsed.note}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-amber-600 ml-2 flex-shrink-0">{tsLabel}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Show move-out date for inactive pets */}
            {petData.status?.toLowerCase() === 'inactive' && petDataAny.move_out_date && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Move-Out Date</label>
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {formatDate(petDataAny.move_out_date)}
                </p>
              </div>
            )}

            {/* Show deactivation reason for inactive pets */}
            {petData.status?.toLowerCase() === 'inactive' && petDataAny.deactivated_reason && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Deactivation Reason</label>
                <p className="text-sm font-semibold text-gray-900 mt-1">{petDataAny.deactivated_reason}</p>
              </div>
            )}

            {/* Deactivate button (hidden when rejected) */}
            {!isRejected && petData?.status?.toLowerCase() !== 'inactive' && (
              <div>
                <p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                  If you want to Deactivate your Pet, click the below button
                </p>
                <button
                  onClick={() => setShowDeactivateConfirm(true)}
                  className="mt-1 flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-colors text-xs sm:text-sm font-medium bg-red-600 text-white hover:bg-red-700"
                >
                  <span className="hidden sm:inline">Deactivate</span>
                </button>
              </div>
            )}
            {petData?.status?.toLowerCase() === 'inactive' && (
              <div>
                <p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
                  This pet profile has been deactivated
                </p>
                <button
                  disabled
                  className="mt-1 flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium bg-gray-300 text-gray-500 cursor-not-allowed"
                >
                  <span className="hidden sm:inline">Inactive</span>
                </button>
              </div>
            )}
          </div>
        );
      }

      case 'documents':
        const isESA = petData.pet_role === 'esa' || petData.animal_type === 'esa';
        const isSA = petData.pet_role === 'sa' || petData.animal_type === 'sa';
        const isInactive = petData?.status?.toLowerCase() === 'inactive';
        // Document upload slots are data-driven from the admin questionnaire
        // (group: 'documents', type: 'file'). q17 keeps the legacy id 'vaccination'
        // so existing uploaded documents continue to match. ESA Letter stays
        // hardcoded for ESA users (option C) — admins cannot toggle it.
        const dataDrivenDocs = (propertySettings?.questionnaire || [])
          .filter((q: any) =>
            q.group === 'documents' &&
            q.type === 'file' &&
            shouldShowQuestion(q)
          )
          .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
          .map((q: any) => ({
            id: q.id === 'q17' ? 'vaccination' : q.id,
            name: q.question,
            description: q.question,
            icon: q.id === 'q17' ? '💉' : '📄',
          }));
        const documentTypes = isSA
          ? []
          : isESA
            ? [
                { id: 'esa-letter', name: 'Emotional Support Animal Letter', description: 'Letter from licensed professional', icon: '📃' },
                ...dataDrivenDocs,
              ]
            : dataDrivenDocs;

        return (
          <div className="space-y-6">
            {petData?.status?.toLowerCase() !== 'inactive' && documents.length > 0 && documents.some(doc => !doc.document_type && !doc.type && !doc.docType) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-blue-900 font-medium">Existing Documents</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Some documents were uploaded before document types were added. To categorize them, delete the old document and re-upload it using the appropriate document type above.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4">
              {documentTypes.map((docType) => {
                // Some upload paths still tag a vaccination record with the
                // raw questionnaire id ``q17`` rather than the legacy free-form
                // string ``vaccination`` that this card renders against (see
                // line ~2628 where ``docType.id`` is remapped from ``q17`` to
                // ``vaccination`` for backwards-compat with pre-questionnaire
                // uploads). Without this alias, a doc stored with
                // ``type='q17'`` -- 9 of them in staging today -- never
                // matches the slot and the resident sees "Upload Document"
                // even though "Document History" below correctly lists the
                // already-uploaded file. Purely additive in the match
                // filter; no upload path or downstream switch is touched.
                const DOC_TYPE_ALIASES: Record<string, string[]> = {
                  vaccination: ['q17'],
                };
                const matchingDocs = documents.filter(doc => {
                  const docTypeValue = doc.type || doc.document_type || doc.docType;
                  if (docTypeValue === docType.id) return true;
                  return (DOC_TYPE_ALIASES[docType.id] || []).includes(docTypeValue);
                });
                const uploadedDoc = matchingDocs
                  .slice()
                  .sort((a, b) => {
                    const ad = new Date(a.uploaded_at || a.created_at || 0).getTime();
                    const bd = new Date(b.uploaded_at || b.created_at || 0).getTime();
                    return bd - ad;
                  })[0];
                const pendingFile = pendingDocFiles[docType.id];
                const hasDocument = !!uploadedDoc || !!pendingFile;

                return (
                  <div key={docType.id} className="relative">
                    {loadingDocuments && !hasDocument ? (
                      <div className="block border-2 border-dashed border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start gap-3">
                          <div className="text-2xl flex-shrink-0 opacity-40">{docType.icon}</div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-400 mb-1">{docType.name}</h4>
                            <p className="text-xs text-gray-400">{docType.description}</p>
                            <div className="mt-2">
                              <LoadingSpinner size="xs" text="" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : !hasDocument ? (
                      <>
                        <input
                          type="file"
                          id={`upload-${docType.id}`}
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleDocumentUpload(e, docType.id, docType.name)}
                          className="hidden"
                          disabled={uploadingDocument || petData?.status?.toLowerCase() === 'inactive'}
                        />
                        <label
                          htmlFor={`upload-${docType.id}`}
                          className={`block border-2 border-dashed rounded-lg p-4 transition-all ${
                            petData?.status?.toLowerCase() === 'inactive'
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                              : 'cursor-pointer border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="text-2xl flex-shrink-0">{docType.icon}</div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-gray-900 mb-1">
                                {docType.name}
                              </h4>
                              <p className="text-xs text-gray-600">{docType.description}</p>
                              <div className="mt-2">
                                <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium">
                                  <Upload className="w-3 h-3" />
                                  Upload Document
                                </span>
                              </div>
                            </div>
                          </div>
                        </label>
                      </>
                    ) : pendingFile ? (
                      <div className="relative block border-2 border-amber-400 bg-amber-50 rounded-lg p-4">
                        <div className="absolute top-2 right-2">
                          <Upload className="w-5 h-5 text-amber-600" />
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="text-2xl flex-shrink-0">{docType.icon}</div>
                          <div className="flex-1 min-w-0 pr-6">
                            <h4 className="text-sm font-medium text-gray-900 mb-1">
                              {docType.name}
                            </h4>
                            <p className="text-xs text-gray-600 mb-2">{docType.description}</p>
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500 rounded-full text-xs text-white font-medium">
                                <Upload className="w-3 h-3" />
                                Ready to Save
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-2 truncate" title={pendingFile.file.name}>
                              {pendingFile.file.name}
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                              {processingDocType === docType.id ? (
                                <div className="px-3 py-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-300 rounded-md inline-flex items-center justify-center">
                                  <LoadingSpinner size="xs" text="" />
                                </div>
                              ) : (
                                <>
                                  <input
                                    type="file"
                                    id={`replace-${docType.id}`}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleDocumentUpload(e, docType.id, docType.name)}
                                    className="hidden"
                                  />
                                  <label
                                    htmlFor={`replace-${docType.id}`}
                                    className="px-3 py-1.5 text-xs text-amber-700 hover:bg-amber-100 border border-amber-300 rounded-md transition-colors cursor-pointer inline-flex items-center gap-1"
                                  >
                                    <Upload className="w-3 h-3" />
                                    Replace
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPendingDocFiles(prev => {
                                        const next = { ...prev };
                                        delete next[docType.id];
                                        return next;
                                      });
                                      if (Object.keys(pendingDocFiles).length <= 1 && !uploadedDoc) {
                                        setDocumentsModified(false);
                                      }
                                    }}
                                    className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 border border-red-300 rounded-md transition-colors inline-flex items-center gap-1"
                                  >
                                    Remove
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="relative block border-2 border-blue-400 bg-blue-50 rounded-lg p-4">
                        <div className="absolute top-2 right-2">
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="text-2xl flex-shrink-0">{docType.icon}</div>
                          <div className="flex-1 min-w-0 pr-6">
                            <h4 className="text-sm font-medium text-gray-900 mb-1">
                              {docType.name}
                            </h4>
                            <p className="text-xs text-gray-600 mb-2">{docType.description}</p>
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-[color:var(--pmc-primary)] rounded-full text-xs text-white font-medium">
                                <CheckCircle className="w-3 h-3" />
                                Uploaded Successfully
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-2 truncate" title={uploadedDoc.name}>
                              {uploadedDoc.name}
                            </p>
                            <div className={`mt-3 flex items-center gap-2 ${processingDocType === docType.id ? 'justify-center' : ''}`}>
                              {processingDocType === docType.id ? (
                                <div className="px-3 py-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-300 rounded-md inline-flex items-center justify-center">
                                  <LoadingSpinner size="xs" text="" />
                                </div>
                              ) : (
                                <>
                                  {uploadedDoc.s3_url && (
                                    <button
                                      type="button"
                                      onClick={() => openPreview(uploadedDoc)}
                                      disabled={previewLoadingId === uploadedDoc.id}
                                      className="px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-100 border border-blue-300 rounded-md transition-colors inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <Eye className="w-3 h-3" />
                                      {previewLoadingId === uploadedDoc.id ? 'Opening...' : 'View'}
                                    </button>
                                  )}
                                  {petData?.status?.toLowerCase() !== 'inactive' && (
                                    <>
                                      <input
                                        type="file"
                                        id={`replace-${docType.id}`}
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => handleDocumentUpload(e, docType.id, docType.name)}
                                        className="hidden"
                                      />
                                      <label
                                        htmlFor={`replace-${docType.id}`}
                                        className="px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-100 border border-blue-300 rounded-md transition-colors cursor-pointer inline-flex items-center gap-1"
                                      >
                                        <Upload className="w-3 h-3" />
                                        Replace
                                      </label>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {loadingDocuments ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="sm" text="Loading documents..." />
              </div>
            ) : documents.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Document History</h4>
                {(() => {
                  const latestByType = {};
                  documents
                    .slice()
                    .sort((a, b) => {
                      const ad = new Date(a.uploaded_at || a.created_at || 0).getTime();
                      const bd = new Date(b.uploaded_at || b.created_at || 0).getTime();
                      return bd - ad;
                    })
                    .forEach((doc) => {
                      const type = doc.document_type || doc.type;
                      if (type && !latestByType[type]) {
                        latestByType[type] = doc;
                      }
                    });
                  
                  return Object.values(latestByType).map((doc) => {
                    const getDocInfo = (docType) => {
                      const type = docType || doc.document_type || doc.type;
                      switch (type) {
                        case 'vaccination':
                          return { icon: '💉', name: 'Vaccination Records' };
                        case 'insurance':
                          return { icon: '🏥', name: 'Pet Insurance' };
                        case 'esa-letter':
                          return { icon: '📃', name: 'Emotional Support Animal Letter' };
                        default:
                          return { icon: null, name: null };
                      }
                    };
                    
                    const docInfo = getDocInfo(doc.document_type);
                    const displayDocType = doc.document_name || docInfo.name;
                    
                    return (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {docInfo.icon ? (
                            <div className="text-xl flex-shrink-0">{docInfo.icon}</div>
                          ) : (
                            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            {displayDocType && (
                              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-0.5">
                                {displayDocType}
                              </p>
                            )}
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {doc.name || doc.file_name || 'Document'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {doc.uploaded_at && `Uploaded ${formatDate(doc.uploaded_at)}`}
                              {!doc.uploaded_at && doc.created_at && `Created ${formatDate(doc.created_at)}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {doc.s3_url && (
                            <button
                              onClick={() => openPreview(doc)}
                              disabled={previewLoadingId === doc.id}
                              className="px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-100 border border-blue-300 rounded-md transition-colors inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="View document"
                            >
                              <Eye className="w-4 h-4" />
                              <span className="hidden sm:inline">{previewLoadingId === doc.id ? 'Opening...' : 'View'}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                {isSA ? (
                  <p className="text-sm">No documents uploaded</p>
                ) : (
                  <>
                    <p className="text-sm">No documents uploaded yet</p>
                    <p className="text-xs mt-1">Upload vaccination records, licenses, or other pet documents</p>
                  </>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
          
          .pet-modal-container {
            font-family: 'Inter', sans-serif;
          }
        `}
      </style>

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4">
        <div className="relative bg-[#eff0f3] border-0 sm:border sm:border-[#cdced7] rounded-none sm:rounded-[10px] p-0 sm:p-[10px] w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] overflow-hidden pet-modal-container"
             style={{ boxShadow: 'none', ...(window.innerWidth >= 640 && { boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)' }) }}>
          
          <div className="flex flex-col md:flex-row h-full sm:h-[600px] gap-0 md:gap-5">
            {/* Left Sidebar - Hidden on mobile, shown as dropdown */}
            <div className="hidden md:flex md:w-48 lg:w-64 flex-col p-[15px]">
              {/* Header with Pet Info */}
              <div className="mb-5">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-[5px]">
                    {getPetIcon(petData.pet_type)}
                    <div className="text-base font-semibold text-[#1e1f24] leading-6">
                      {petData.pet_name || 'Fido'}
                    </div>
                  </div>
                  <div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      petData.status === 'Active' ? 'bg-green-100 text-green-800' :
                      petData.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      petData.status?.toLowerCase() === 'inactive' ? 'bg-red-100 text-red-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      <CheckCircle className="w-3 h-3" />
                      {petData.status?.toLowerCase() === 'inactive' ? 'Inactive' : status}
                    </span>
                  </div>
                </div>
                {(petData.pet_role === 'esa' || petData.pet_role === 'sa') && (
                  <div className="mt-2">
                    <div className="bg-[#d72681] rounded-[5px] h-6 flex items-center px-[5px] w-fit">
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                        <span className="text-xs font-semibold text-white leading-5">
                          {petData.pet_role === 'esa' ? 'Emotional Support Animal' : 'Service Animal'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Menu */}
              <div className="flex flex-col gap-[10px]">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedSection(item.id)}
                    className={`w-full h-10 rounded-lg flex items-center px-[10px] transition-all text-left ${
                      item.active
                        ? 'bg-[color:var(--pmc-primary-tint)] text-[color:var(--pmc-primary)]'
                        : 'hover:bg-gray-100 text-[#1e1f24]'
                    }`}
                  >
                    <span className="text-sm leading-5 font-medium truncate" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 bg-white rounded-none sm:rounded-[10px] flex flex-col min-h-0">
              {/* Header */}
              <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  {/* Mobile: Section Selector Dropdown */}
                  <div className="md:hidden flex-1">
                    <select
                      value={selectedSection}
                      onChange={(e) => setSelectedSection(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-purple-500"
                    >
                      {navigationItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Desktop: Title and Description */}
                  <div className="hidden md:block flex-1 min-w-0">
                    <h2 className="text-sm font-semibold text-[#1e1f24] truncate">
                      {navigationItems.find(item => item.id === selectedSection)?.label}
                    </h2>
                    <p className="text-xs text-[#62636c] truncate">
                      {selectedSection === 'esaInfo' ? 'Animal type classification and documentation' :
                       selectedSection === 'petInfo' ? 'Basic pet information and details' :
                       selectedSection === 'vetInfo' ? 'Veterinary contact and visit information' :
                       selectedSection === 'propertyInfo' ? 'Property and contact details' :
                       selectedSection === 'documents' ? 'Upload and manage pet documents' :
                       'Registration status and details'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {selectedSection === 'documents' && documentsModified && (
                    <button
                      onClick={async () => {
                        if (Object.keys(pendingDocFiles).length === 0) {
                          setDocumentsModified(false);
                          return;
                        }
                        try {
                          setUploadingDocument(true);
                          for (const [docType, cached] of Object.entries(pendingDocFiles)) {
                            setProcessingDocType(docType);
                            await petService.uploadPetDocuments(petData.id, [cached.file], docType, cached.docName);
                          }
                          setPendingDocFiles({});

                          // Re-approval / resubmission logic
                          const isApproved = petData?.status === 'Active' || petData?.status === 'active';
                          const isCurrentlyRejected = petData?.status === 'Rejected' || petData?.status === 'rejected';
                          const hasESADocs = modifiedDocTypes.has('esa-letter');
                          const hasRegularDocs = [...modifiedDocTypes].some(dt => dt !== 'esa-letter');
                          const needsReapproval = isApproved && (
                            (hasESADocs && propertySettings?.general?.requireReapprovalESA === true) ||
                            (hasRegularDocs && propertySettings?.general?.requireReapprovalDocuments === true)
                          );
                          const needsResubmit = isCurrentlyRejected;

                          if (needsReapproval || needsResubmit) {
                            await petService.updatePet(petData.id, { status: 'Pending' });
                            setStatus('Pending');
                            if (onPetUpdated) {
                              const freshPet = await petService.getPetDetails(petData.id);
                              if (freshPet) await onPetUpdated(freshPet);
                            }
                          }

                          setDocumentsModified(false);
                          setModifiedDocTypes(new Set());
                          await fetchDocuments();
                          if (showToast) {
                            if (needsResubmit) {
                              showToast('Documents updated and registration resubmitted for review.', 'success');
                            } else if (needsReapproval) {
                              showToast('Documents saved. Pet status moved to Pending for re-approval.', 'info');
                            } else {
                              showToast('Documents saved successfully!', 'success');
                            }
                          }
                        } catch (error: any) {
                          console.error('Error saving documents:', error);
                          if (showToast) {
                            showToast('Failed to save documents. Please try again.', 'error');
                          }
                        } finally {
                          setProcessingDocType(null);
                          setUploadingDocument(false);
                        }
                      }}
                      disabled={uploadingDocument}
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs sm:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">{uploadingDocument ? 'Saving...' : (petData?.status === 'Rejected' ? 'Save & Resubmit' : 'Save')}</span>
                    </button>
                  )}
                  {/* Show locked indicator when sections are locked (Active, or ESA/SA submitted) */}
                  {(isPetInfoLocked && (selectedSection === 'petInfo' || selectedSection === 'esaInfo' || selectedSection === 'propertyInfo' || selectedSection === 'registrationDetails')) && (
                    <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 text-gray-500 rounded-md text-xs sm:text-sm font-medium cursor-not-allowed" title={isEsaSa ? "This section is locked after submission. Only veterinary information and documents can be edited." : "This section is locked after approval. Only veterinary information and documents can be edited."}>
                      <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Locked</span>
                    </div>
                  )}
                  {selectedSection !== 'documents' && selectedSection !== 'registrationDetails' && selectedSection !== 'propertyInfo' && selectedSection !== 'esaInfo' && !(isPetInfoLocked && selectedSection === 'petInfo') && (
                    <>
                      {!isEditMode ? (
                        <>
                          {petData?.status?.toLowerCase() !== 'inactive' && (
                            <>
                              <button
                                onClick={() => { setFieldErrors({}); setIsEditMode(true); }}
                                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-[color:var(--pmc-primary)] text-white rounded-md hover:bg-[color:var(--pmc-primary)] transition-colors text-xs sm:text-sm font-medium"
                              >
                                <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Edit</span>
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleCancel}
                            className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-xs sm:text-sm font-medium"
                            disabled={isSaving}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSave}
                            disabled={isSaving || hasValidationErrors()}
                            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs sm:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isSaving ? (
                              <>
                                <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                <span className="hidden sm:inline">Saving...</span>
                              </>
                            ) : (
                              <>
                                <Save className="w-3 h-3 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">{isRejected ? 'Save & Resubmit' : 'Save'}</span>
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </>
                  )}
                  <button
                    onClick={handleClose}
                    className="w-7 h-7 sm:w-8 sm:h-8 bg-white border border-[#b9bbc6] rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
                    style={{ boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)' }}
                  >
                    <X className="w-4 h-4 text-[#1e1f24]" />
                  </button>
                </div>
              </div>

              {/* Mobile: Pet Info Banner */}
              <div className="md:hidden px-4 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getPetIcon(petData.pet_type)}
                    <span className="text-sm font-semibold text-[#1e1f24]">
                      {petData.pet_name || 'Fido'}
                    </span>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    petData.status === 'Active' ? 'bg-green-100 text-green-800' :
                    petData.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                    petData.status?.toLowerCase() === 'inactive' ? 'bg-red-100 text-red-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    <CheckCircle className="w-3 h-3" />
                    {petData.status?.toLowerCase() === 'inactive' ? 'Inactive' : status}
                  </span>
                </div>
                {(petData.pet_role === 'esa' || petData.pet_role === 'sa') && (
                  <div className="mt-2">
                    <div className="bg-[#d72681] rounded-[5px] h-6 flex items-center px-2 w-fit">
                      <span className="text-xs font-semibold text-white">
                        {petData.pet_role === 'esa' ? 'Emotional Support Animal' : 'Service Animal'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 p-4 sm:p-5 overflow-y-auto min-h-0">
                {renderSectionContent()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Pet Modal */}
      {showDeactivateConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Deactivate Profile</h3>
              <button onClick={() => setShowDeactivateConfirm(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4 sm:space-y-5">
              {/* Move-Out Date (Required) */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Move-Out Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={deactivateMoveOutDate}
                  onChange={(e) => setDeactivateMoveOutDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
                  required
                />
              </div>

              {/* Reason for Deactivation (Required Dropdown) */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Reason for Deactivation <span className="text-red-500">*</span>
                </label>
                <select
                  value={deactivateReason}
                  onChange={(e) => setDeactivateReason(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors"
                  required
                >
                  <option value="">Select a reason...</option>
                  <option value="Moved Out">Moved Out</option>
                  <option value="Pet Deceased">Pet Deceased</option>
                  <option value="Rehomed">Rehomed</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Notes (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={deactivateNotes}
                  onChange={(e) => setDeactivateNotes(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Additional context or information..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 transition-colors resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{deactivateNotes.length}/500 characters</p>
              </div>

              {/* Confirmation Checkbox */}
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={deactivateAcknowledge}
                  onChange={(e) => setDeactivateAcknowledge(e.target.checked)}
                  className="mt-1 w-4 h-4 text-[color:var(--pmc-primary)] focus:ring-[color:var(--pmc-primary)] flex-shrink-0"
                />
                <span className="text-sm sm:text-base text-gray-800">
                  I confirm {petData?.pet_name || 'this pet'} no longer lives with me. I understand PawTection will notify the property manager of this change.
                </span>
              </label>
            </div>
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-5 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowDeactivateConfirm(false)}
                className="w-full sm:w-auto px-5 py-2 rounded-full border border-[#f4b95a] text-[#f4b95a] hover:bg-[#fff7e9] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivate}
                disabled={!deactivateReason || !deactivateMoveOutDate || !deactivateAcknowledge}
                className={`w-full sm:w-auto px-5 py-2 rounded-full text-white transition-colors ${(!deactivateReason || !deactivateMoveOutDate || !deactivateAcknowledge)
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'}`}
              >
                Deactivate Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Document Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleDeleteDocument}
        title="Delete Document"
        message={`Are you sure you want to delete ${documentToDelete?.file_name || 'this document'}? This action cannot be undone.`}
        confirmText="Delete Document"
        cancelText="Cancel"
        type="danger"
      />

      {/* Document Preview Modal */}
      {showPreview && previewDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4" onClick={closePreview}>
          <div className="bg-white rounded-none sm:rounded-lg w-full h-full sm:h-auto sm:max-w-5xl sm:max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate flex-1 mr-2">
                {previewDoc.document_name || previewDoc.name || previewDoc.file_name || 'Document Preview'}
              </h3>
              <button
                onClick={closePreview}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 p-4 overflow-auto min-h-0">
              {(() => {
                const url = (previewDoc as any).s3_url || (previewDoc as any).url;
                const name = (previewDoc.name || (previewDoc as any).file_name || '').toLowerCase();
                const type = (previewDoc.mimeType || (previewDoc as any).mime_type || (previewDoc as any).content_type || '').toLowerCase();
                const isImage = type.startsWith('image/') || /(\.png|\.jpg|\.jpeg|\.gif|\.webp)$/.test(name);
                const isPdf = type === 'application/pdf' || name.endsWith('.pdf');
                const isOffice = /(\.doc|\.docx|\.xls|\.xlsx|\.ppt|\.pptx)$/.test(name);

                if (isImage) {
                  return <img src={url} alt={previewDoc.name || (previewDoc as any).file_name} className="max-w-full h-auto mx-auto" />;
                }
                if (isPdf) {
                  return <iframe src={url} className="w-full h-full min-h-[400px] sm:min-h-[600px] border-0" title={previewDoc.name || (previewDoc as any).file_name} />;
                }
                if (isOffice) {
                  const gview = `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`;
                  return <iframe src={gview} className="w-full h-full min-h-[400px] sm:min-h-[600px] border-0" title={previewDoc.name || (previewDoc as any).file_name} />;
                }
                return (
                  <div className="space-y-3">
                    <iframe src={url} className="w-full h-full min-h-[400px] sm:min-h-[600px] border-0" title={previewDoc.name || (previewDoc as any).file_name} />
                    <div className="text-center">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">Open in new tab</a>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Image Carousel Modal */}
      {isCarouselOpen && carouselImages.length > 0 && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <button
              onClick={() => setIsCarouselOpen(false)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>

            {/* Previous Button */}
            {carouselImages.length > 1 && (
              <button
                onClick={prevImage}
                className="absolute left-4 p-3 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
            )}

            {/* Image Display */}
            <div className="max-w-5xl max-h-[90vh] flex flex-col items-center">
              <img
                src={carouselImages[currentImageIndex]?.url}
                alt={`Pet photo ${currentImageIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />

              {/* Image Counter */}
              <div className="mt-4 px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-700">
                {currentImageIndex + 1} / {carouselImages.length}
              </div>
            </div>

            {/* Next Button */}
            {carouselImages.length > 1 && (
              <button
                onClick={nextImage}
                className="absolute right-4 p-3 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
              >
                <ChevronRight className="w-6 h-6 text-gray-700" />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PetDetailsModal;